import React from 'react';
import {
  Bold,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Filter,
  Heading1,
  Heading2,
  Italic,
  Link2,
  List,
  MessageSquareQuote,
  MoreHorizontal,
  Plus,
  Search,
  Save,
  X,
} from 'lucide-react';
import styled from 'styled-components';
import BaseCard from '../../../../components/Card';
import { backendPalette } from '../../../../components/backend/BackendCardSystem';
import GlassPicklist, { type GlassPicklistOption } from '../../../../components/ui/GlassPicklist';
import { apiFetch } from '../../../../lib/api';

type ProcessStatus = 'Verified' | 'Draft' | 'Needs Review';
type SortMode = 'recent' | 'title' | 'service';
type ProcessTypeFilter = 'all' | KnowledgeProcessType;
type BookmarkFilter = 'all' | 'bookmarked';
type InspectorTabKey = 'details' | 'execution' | 'guardrails' | 'revisions' | 'resolution';
type PublicCatalogCategory = 'platform' | 'marketing' | 'assets_design' | 'custom' | string;
type KnowledgeCategorySource = 'service' | 'custom';
type KnowledgeProcessType = 'service' | 'internal';
type KnowledgeListFieldKey =
  | 'prerequisites'
  | 'requiredInputs'
  | 'expectedOutputs'
  | 'toolsAndPlatforms'
  | 'orderedSteps'
  | 'qaChecklist'
  | 'definitionOfDone'
  | 'hardRules'
  | 'doNotDo'
  | 'approvalRequirements'
  | 'escalationTriggers'
  | 'handoffRules'
  | 'riskNotes';

type KnowledgeProcess = {
  id: string;
  processType: KnowledgeProcessType;
  serviceId: string | null;
  title: string;
  serviceCode: string | null;
  serviceLabel: string | null;
  offeringCode: string | null;
  category: string;
  subcategoryId: string | null;
  subcategories: string[];
  tags: string[];
  status: ProcessStatus;
  summary: string;
  objective: string;
  scope: string;
  prerequisites: string[];
  requiredInputs: string[];
  expectedOutputs: string[];
  toolsAndPlatforms: string[];
  orderedSteps: string[];
  qaChecklist: string[];
  definitionOfDone: string[];
  hardRules: string[];
  doNotDo: string[];
  approvalRequirements: string[];
  escalationTriggers: string[];
  handoffRules: string[];
  riskNotes: string[];
  bodyHtml: string;
  linkedIds: string[];
  isBookmarked: boolean;
  contributors: Array<{ id: string; name: string; role: 'admin' | 'agent'; edits: number }>;
  revisions: Array<{ id: string; author: string; changedAt: string; summary: string }>;
};

type CatalogTierDefinition = {
  metadata?: Record<string, unknown>;
  offerings?: readonly CatalogServiceOfferingDefinition[];
};

type CatalogServiceOfferingSubcategoryDefinition = {
  id: string;
  name: string;
  description?: string | null;
};

type CatalogServiceOfferingDefinition = {
  id?: string;
  code: string;
  name: string;
  description?: string | null;
  status?: string | null;
  sortOrder?: number | null;
  metadata?: Record<string, unknown>;
};

type CatalogServiceDefinition = {
  id?: string;
  code?: string;
  category: PublicCatalogCategory;
  title: string;
  metadata?: Record<string, unknown>;
  tiers?: readonly CatalogTierDefinition[];
};

type PublicCatalogServicesResponse = {
  services?: CatalogServiceDefinition[];
};

type KBaseProcessesResponse = {
  processes?: KnowledgeProcess[];
};

type ServiceOfferingCatalogEntry = {
  code: string;
  name: string;
  description: string;
  status: string;
  sortOrder: number;
  type: string;
  addOnEnabled: boolean;
  unitPriceCents: number | null;
  sla: Record<string, unknown>;
  subcategories: CatalogServiceOfferingSubcategoryDefinition[];
};

type KnowledgeCategoryOption = {
  label: string;
  normalizedLabel: string;
  source: KnowledgeCategorySource;
  serviceCode: string | null;
  serviceTitle: string | null;
  offeringCode: string | null;
};

type CustomKnowledgeCategory = {
  serviceCode: string | null;
  serviceLabel: string | null;
  label: string;
};

type CreateProcessDraft = {
  type: KnowledgeProcessType | null;
  title: string;
  serviceCode: string;
  offeringCode: string;
  selectedSubcategoryId: string;
  selectedSubcategoryName: string;
  selectedSubcategoryDescription: string;
  subcategoryNameDraft: string;
  subcategoryDescriptionDraft: string;
  showSubcategoryComposer: boolean;
  tags: string[];
  tagQuery: string;
  error: string;
  saving: boolean;
};

const PAGE_SIZE = 20;
const DEFAULT_OFFERING_SLA = {
  responseTargetMinutes: null,
  resolutionTargetMinutes: null,
  targetCompletionMinutes: null,
  escalationAfterMinutes: null,
  escalationScope: null,
  queueWeight: null,
  businessHoursOnly: null,
} satisfies Record<string, unknown>;

const createInitialCreateProcessDraft = (): CreateProcessDraft => ({
  type: null,
  title: '',
  serviceCode: '',
  offeringCode: '',
  selectedSubcategoryId: '',
  selectedSubcategoryName: '',
  selectedSubcategoryDescription: '',
  subcategoryNameDraft: '',
  subcategoryDescriptionDraft: '',
  showSubcategoryComposer: false,
  tags: [],
  tagQuery: '',
  error: '',
  saving: false,
});

const DEFAULT_PROCESS_TYPE_FILTER: ProcessTypeFilter = 'all';
const DEFAULT_BOOKMARK_FILTER: BookmarkFilter = 'all';

const DEFAULT_EXECUTION_FIELDS = {
  objective: '',
  scope: '',
  prerequisites: [] as string[],
  requiredInputs: [] as string[],
  expectedOutputs: [] as string[],
  toolsAndPlatforms: [] as string[],
  orderedSteps: [] as string[],
  qaChecklist: [] as string[],
  definitionOfDone: [] as string[],
};

const DEFAULT_GUARDRAIL_FIELDS = {
  hardRules: [] as string[],
  doNotDo: [] as string[],
  approvalRequirements: [] as string[],
  escalationTriggers: [] as string[],
  handoffRules: [] as string[],
  riskNotes: [] as string[],
};

const EXECUTION_TEXT_FIELDS = [
  { key: 'objective' as const, label: 'Objective', placeholder: 'What should this process achieve?' },
  { key: 'scope' as const, label: 'Scope', placeholder: 'When should this process be used?' },
];

const EXECUTION_LIST_FIELDS: Array<{ key: KnowledgeListFieldKey; label: string; placeholder: string }> = [
  { key: 'prerequisites', label: 'Prerequisites', placeholder: 'One prerequisite per line' },
  { key: 'requiredInputs', label: 'Required Inputs', placeholder: 'One required input per line' },
  { key: 'expectedOutputs', label: 'Expected Outputs', placeholder: 'One expected output per line' },
  { key: 'toolsAndPlatforms', label: 'Tools And Platforms', placeholder: 'One tool or platform per line' },
  { key: 'orderedSteps', label: 'Ordered Steps', placeholder: 'One step per line in execution order' },
  { key: 'qaChecklist', label: 'QA Checklist', placeholder: 'One QA check per line' },
  { key: 'definitionOfDone', label: 'Definition Of Done', placeholder: 'One completion criterion per line' },
];

const GUARDRAIL_LIST_FIELDS: Array<{ key: KnowledgeListFieldKey; label: string; placeholder: string }> = [
  { key: 'hardRules', label: 'Hard Rules', placeholder: 'One non-negotiable rule per line' },
  { key: 'doNotDo', label: 'Do Not Do', placeholder: 'One forbidden action per line' },
  { key: 'approvalRequirements', label: 'Approval Requirements', placeholder: 'One approval requirement per line' },
  { key: 'escalationTriggers', label: 'Escalation Triggers', placeholder: 'One escalation trigger per line' },
  { key: 'handoffRules', label: 'Handoff Rules', placeholder: 'One handoff rule per line' },
  { key: 'riskNotes', label: 'Risk Notes', placeholder: 'One risk note per line' },
];

const formatListField = (values: readonly string[]) => values.join('\n');

const parseListField = (value: string) =>
  [...new Set(
    value
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean),
  )];

const Page = styled.section`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  align-content: start;
  gap: 12px;
  min-width: 0;
  min-height: calc(100dvh - 2.5rem);
  height: calc(100dvh - 2.5rem);
  padding: ${({ theme }) => theme.spacing(0)};
  @supports not (height: 100dvh) {
    height: calc(100vh - 2.5rem);
  }
`;

const Header = styled(BaseCard)`
  padding: 12px;
  display: grid;
  align-content: start;
  gap: 4px;
  min-width: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
`;

const Eyebrow = styled.div`
  color: #9aa4b2;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: .28px;
  text-transform: uppercase;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  color: #E8EAED;
  font-size: clamp(1.5rem, 2.2vw, 2rem);
  line-height: 1;
  font-weight: 900;
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 980px;
  color: #9aa4b2;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 700;
`;

const Workspace = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
  min-height: 0;
  height: 100%;
  grid-template-columns: clamp(280px, 24vw, 360px) minmax(0, 1fr);

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const Panel = styled(BaseCard)`
  padding: 0;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: grid;
  overflow: hidden;
`;

const LibraryPanel = styled(Panel)`
  grid-template-rows: auto auto minmax(0, 1fr) auto;
`;

const WorkspacePanel = styled(Panel)`
  grid-template-rows: auto minmax(0, 1fr);
`;

const PanelHeader = styled.div`
  padding: 12px;
  display: grid;
  gap: 2px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const PanelTitle = styled.h2`
  margin: 0;
  color: #E8EAED;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: .3px;
  text-transform: uppercase;
`;

const PanelCopy = styled.p`
  margin: 0;
  color: #9aa4b2;
  font-size: 11px;
  line-height: 1.45;
  font-weight: 700;
`;

const PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Toolbar = styled.div`
  padding: 10px 12px;
  display: grid;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const SearchWrap = styled.label`
  position: relative;
  display: block;
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: stretch;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.38);
`;

const Input = styled.input`
  width: 100%;
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, 0.04);
  color: #E8EAED;
  padding: 0 14px 0 40px;
  font-size: 13px;
  font-weight: 700;

  &::placeholder {
    color: #9aa4b2;
  }

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .15);
  }
`;

const FilterButton = styled.button`
  min-width: 38px;
  width: 38px;
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, 0.04);
  color: #E8EAED;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, .55);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }
`;

const FilterCountBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(224, 161, 94, 0.96);
  color: #111;
  font-size: 0.64rem;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const PicklistControl = styled.div`
  min-height: 38px;

  & > div {
    height: 100%;
  }

  & > div > button {
    min-height: 38px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, .14) !important;
    background: rgba(255, 255, 255, 0.04);
    padding: 0 30px 0 11px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .2px;
  }

  & > div > button:hover {
    border-color: rgba(224, 161, 94, .55) !important;
  }

  & > div > button:focus,
  & > div > button:focus-visible {
    border-color: rgba(224, 161, 94, .75) !important;
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }

  & > div > span {
    right: 10px;
    font-size: .66rem;
  }
`;

const LibraryBody = styled.div`
  padding: 8px 12px 12px;
  display: grid;
  gap: 8px;
  min-height: 0;
  overflow: auto;
  align-content: start;
`;

const ProcessButton = styled.button<{ $active: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .06);
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, .14)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#E8EAED' : '#cbd2e1')};
  padding: 10px;
  display: grid;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, .07);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .15);
  }
`;

const ProcessTagRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
`;

const ProcessTagChip = styled.span`
  min-width: 0;
  max-width: 140px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(224,161,94,0.1);
  color: rgba(255,255,255,0.8);
  font-size: 0.68rem;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Pagination = styled.div`
  padding: 10px 12px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SmallButton = styled.button<{ $accent?: boolean }>`
  min-height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${({ $accent }) => ($accent ? 'rgba(224, 161, 94, .45)' : 'rgba(255, 255, 255, .14)')};
  background: ${({ $accent }) => ($accent ? 'rgba(224, 161, 94, .14)' : 'rgba(255, 255, 255, .04)')};
  color: #E8EAED;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .2px;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, .55);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }

  &:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }
`;

const TabBar = styled.div`
  padding: 8px 12px 0;
  display: flex;
  gap: 6px;
  overflow: auto;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 0 0 240px;
  width: 240px;
  min-height: 36px;
  padding: 0 10px;
  border-radius: 10px 10px 0 0;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, .34)' : 'rgba(255, 255, 255, .08)')};
  border-bottom-color: transparent;
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, .1)' : 'rgba(255, 255, 255, .02)')};
  color: #E8EAED;
  cursor: pointer;
`;

const StatusMenuWrap = styled.div`
  position: relative;
  min-width: 180px;
`;

const ActionMenuWrap = styled.div`
  position: relative;
`;

const StatusMenuTrigger = styled.button<{ $open: boolean }>`
  width: 100%;
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid ${({ $open }) => ($open ? 'rgba(224, 161, 94, .6)' : 'rgba(255, 255, 255, .14)')};
  background: rgba(255, 255, 255, 0.04);
  color: #E8EAED;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .2px;
  text-transform: uppercase;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }
`;

const ActionMenuTrigger = styled.button<{ $open: boolean }>`
  min-width: 40px;
  width: 40px;
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid ${({ $open }) => ($open ? 'rgba(224, 161, 94, .6)' : 'rgba(255, 255, 255, .14)')};
  background: rgba(255, 255, 255, 0.04);
  color: #E8EAED;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }
`;

const StatusMenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  z-index: 8;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(17, 22, 33, 0.92);
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  padding: 6px;
  display: grid;
  gap: 4px;
`;

const StatusMenuOption = styled.button<{ $selected?: boolean; $danger?: boolean }>`
  width: 100%;
  min-height: 34px;
  border-radius: 8px;
  border: 1px solid transparent;
  background: ${({ $selected, $danger }) =>
    $danger
      ? 'rgba(255, 107, 107, 0.12)'
      : $selected
        ? 'rgba(255, 255, 255, 0.14)'
        : 'transparent'};
  color: ${({ $danger }) => ($danger ? '#FF8A8A' : 'rgba(248, 250, 255, 0.96)')};
  padding: 7px 10px;
  text-align: left;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: ${({ $danger }) => ($danger ? 'rgba(255, 107, 107, 0.18)' : 'rgba(255, 255, 255, 0.1)')};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${({ $danger }) => ($danger ? 'rgba(255, 107, 107, 0.5)' : 'rgba(255, 255, 255, 0.45)')};
  }
`;

const ReadOnlyField = styled.div`
  display: grid;
  gap: 6px;
  align-content: start;
`;

const ReadOnlyValue = styled.div`
  color: #E8EAED;
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1.35;
`;

const WorkspaceBody = styled.div`
  min-height: 0;
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.6fr);

  @media (max-width: 1260px) {
    grid-template-columns: 1fr;
  }
`;

const EditorColumn = styled.div`
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-rows: auto auto auto minmax(0, 1fr);
  overflow: hidden;
  border-right: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 1260px) {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const Inspector = styled.div`
  padding: 12px;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 10px;
  overflow: hidden;
`;

const InspectorTabs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
`;

const InspectorTabButton = styled.button<{ $active: boolean }>`
  min-height: 38px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, .45)' : 'rgba(255,255,255,.12)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, .14)' : 'rgba(255,255,255,.04)')};
  color: #E8EAED;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .2px;
  text-transform: uppercase;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, .75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .14);
  }
`;

const EditorToolbar = styled.div`
  padding: 10px 12px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const PlainInput = styled.input`
  width: 100%;
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, 0.04);
  color: #E8EAED;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 700;

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .15);
  }
`;

const PlainTextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  resize: vertical;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .14);
  background: rgba(255, 255, 255, 0.04);
  color: #E8EAED;
  padding: 12px;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 700;

  &:focus {
    outline: none;
    border-color: rgba(224, 161, 94, .7);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, .15);
  }
`;

const EditorSurface = styled.div`
  min-height: 0;
  margin: 0 12px 12px;
  padding: 18px 18px 28px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, .08);
  background: rgba(255, 255, 255, 0.02);
  color: #E8EAED;
  font-size: 14px;
  line-height: 1.7;
  outline: none;
  overflow: auto;
  overscroll-behavior: contain;

  h1, h2 { color: rgba(255, 255, 255, 0.97); margin: 0 0 0.8rem; }
  p { margin: 0 0 1rem; }
  ul { margin: 0 0 1rem 1.2rem; padding: 0; }
  blockquote {
    margin: 0 0 1rem;
    padding: 0.9rem 1rem;
    border-left: 3px solid ${backendPalette.yellow};
    background: rgba(224, 161, 94, 0.08);
  }
`;

const InspectorCard = styled.div`
  min-height: 0;
  height: 100%;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .06);
  background: transparent;
  padding: 14px 14px 22px;
  display: grid;
  gap: 10px;
  align-content: start;
  overflow: auto;
  overscroll-behavior: contain;
`;

const ModalBackdrop = styled.button<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  border: 0;
  padding: 0;
  margin: 0;
  background: rgba(5, 8, 16, 0.72);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 160ms ease;
  z-index: 70;
`;

const ModalPanel = styled.section<{ $open: boolean }>`
  position: fixed;
  inset: 50% auto auto 50%;
  width: min(760px, calc(100vw - 32px));
  max-height: min(820px, calc(100dvh - 32px));
  transform: translate(-50%, -50%) scale(${({ $open }) => ($open ? 1 : 0.98)});
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 160ms ease, transform 160ms ease;
  z-index: 71;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  padding: 0;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    linear-gradient(180deg, rgba(18, 24, 38, 0.96), rgba(8, 13, 24, 0.98)),
    radial-gradient(120% 180% at 100% 0%, rgba(224, 161, 94, 0.08), transparent 46%);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.48);
  backdrop-filter: blur(18px);

  @supports not (max-height: 100dvh) {
    max-height: calc(100vh - 32px);
  }
`;

const ModalHeader = styled.div`
  padding: 16px 18px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  gap: 4px;
`;

const ModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #e8eaed;
  font-size: 1rem;
  font-weight: 900;
  letter-spacing: 0.2px;
  text-transform: uppercase;
`;

const ModalBody = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 16px 18px;
  display: grid;
  gap: 16px;
  align-content: start;
`;

const ModalFooter = styled.div`
  padding: 12px 18px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const ModalSection = styled.div`
  display: grid;
  gap: 10px;
`;

const ModalLabel = styled.div`
  color: #e8eaed;
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.24px;
  text-transform: uppercase;
`;

const ModalHelp = styled.div`
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.76rem;
  line-height: 1.45;
  font-weight: 700;
`;

const TypeGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const TypeOption = styled.button<{ $active: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.5)' : 'rgba(255, 255, 255, 0.1)')};
  background: ${({ $active }) => ($active ? 'rgba(224, 161, 94, 0.12)' : 'rgba(255, 255, 255, 0.03)')};
  color: #e8eaed;
  padding: 14px;
  display: grid;
  gap: 6px;
  cursor: pointer;

  &:hover {
    border-color: rgba(224, 161, 94, 0.55);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: rgba(224, 161, 94, 0.75);
    box-shadow: 0 0 0 2px rgba(224, 161, 94, 0.14);
  }
`;

const DangerButton = styled(SmallButton)`
  border-color: rgba(255, 107, 107, 0.4);
  background: rgba(255, 107, 107, 0.12);
  color: #ffb0b0;

  &:hover {
    border-color: rgba(255, 107, 107, 0.58);
  }

  &:focus,
  &:focus-visible {
    border-color: rgba(255, 107, 107, 0.7);
    box-shadow: 0 0 0 2px rgba(255, 107, 107, 0.14);
  }
`;

const CreateGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const TokenWrap = styled.div`
  display: grid;
  gap: 8px;
`;

const TokenRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const InlineRow = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const InlineAction = styled(SmallButton)`
  min-width: 38px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TokenChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(224, 161, 94, 0.28);
  background: rgba(224, 161, 94, 0.12);
  color: #e8eaed;
  font-size: 0.72rem;
  font-weight: 800;
  cursor: pointer;
`;

const SuggestionList = styled.div`
  max-height: 180px;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  display: grid;
`;

const SuggestionButton = styled.button<{ $accent?: boolean }>`
  width: 100%;
  text-align: left;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: ${({ $accent }) => ($accent ? 'rgba(224, 161, 94, 0.08)' : 'transparent')};
  color: #e8eaed;
  padding: 10px 12px;
  display: grid;
  gap: 2px;
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const ErrorText = styled.div`
  color: #ffb48a;
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.45;
`;

const asRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
};

const normalizeText = (value: string) => value.trim().toLowerCase();

const makeSubcategoryId = (offeringCode: string, subcategoryName: string) =>
  `${offeringCode}_${subcategoryName}`
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

const makeCategoryRegistryKey = (serviceCode: string | null, categoryLabel: string) =>
  `${(serviceCode ?? '__global__').trim()}::${normalizeText(categoryLabel)}`;

const dedupeStrings = (values: readonly string[]) => {
  const seen = new Set<string>();
  const next: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const normalized = normalizeText(trimmed);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    next.push(trimmed);
  }
  return next;
};

const readServiceLabel = (service: CatalogServiceDefinition) =>
  typeof service.title === 'string' && service.title.trim()
    ? service.title.trim()
    : String(service.category);

const readOfferingSubcategories = (
  value: unknown,
  offeringCode: string,
): CatalogServiceOfferingSubcategoryDefinition[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const next: CatalogServiceOfferingSubcategoryDefinition[] = [];

  value.forEach((entry, index) => {
    const raw = asRecord(entry);
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    if (!name) return;
    const id =
      (typeof raw.id === 'string' ? raw.id.trim() : '')
      || makeSubcategoryId(offeringCode, name)
      || `${offeringCode}_SUBCATEGORY_${index + 1}`;
    if (seen.has(id)) return;
    seen.add(id);
    next.push({
      id,
      name,
      description:
        typeof raw.description === 'string' ? raw.description.trim() : '',
    });
  });

  return next;
};

const readMetadataOfferingsCatalog = (
  service: CatalogServiceDefinition,
): ServiceOfferingCatalogEntry[] => {
  const offeringsCatalog = Array.isArray(service.metadata?.offeringsCatalog)
    ? service.metadata.offeringsCatalog
    : [];

  return offeringsCatalog
    .map((rawEntry, index) => {
      const entry = asRecord(rawEntry);
      const code = typeof entry.code === 'string' ? entry.code.trim() : '';
      const name = typeof entry.name === 'string' ? entry.name.trim() : '';
      if (!code || !name) return null;
      const status =
        typeof entry.status === 'string' && entry.status.trim()
          ? entry.status.trim()
          : 'published';
      if (normalizeText(status) === 'archived') return null;
      return {
        code,
        name,
        description:
          typeof entry.description === 'string' ? entry.description.trim() : '',
        status,
        sortOrder:
          typeof entry.sortOrder === 'number' && Number.isFinite(entry.sortOrder)
            ? entry.sortOrder
            : index,
        type:
          typeof entry.type === 'string' && entry.type.trim()
            ? entry.type.trim()
            : 'core',
        addOnEnabled: Boolean(entry.addOnEnabled),
        unitPriceCents:
          typeof entry.unitPriceCents === 'number' && Number.isFinite(entry.unitPriceCents)
            ? entry.unitPriceCents
            : null,
        sla: asRecord(entry.sla),
        subcategories: readOfferingSubcategories(entry.subcategories, code),
      } satisfies ServiceOfferingCatalogEntry;
    })
    .filter((entry): entry is ServiceOfferingCatalogEntry => Boolean(entry));
};

const readServiceOfferingCatalog = (
  service: CatalogServiceDefinition,
): ServiceOfferingCatalogEntry[] => {
  const metadataByCode = new Map(
    readMetadataOfferingsCatalog(service).map((entry) => [entry.code, entry] as const),
  );
  const offeringMap = new Map<string, ServiceOfferingCatalogEntry>();

  for (const tier of service.tiers ?? []) {
    for (const rawOffering of tier.offerings ?? []) {
      const code = typeof rawOffering.code === 'string' ? rawOffering.code.trim() : '';
      const name = typeof rawOffering.name === 'string' ? rawOffering.name.trim() : '';
      if (!code || !name || offeringMap.has(code)) continue;
      const status =
        typeof rawOffering.status === 'string' && rawOffering.status.trim()
          ? rawOffering.status.trim()
          : 'published';
      if (normalizeText(status) === 'archived') continue;
      const metadataEntry = metadataByCode.get(code);
      offeringMap.set(code, {
        code,
        name,
        description:
          typeof rawOffering.description === 'string' && rawOffering.description.trim()
            ? rawOffering.description.trim()
            : metadataEntry?.description ?? '',
        status,
        sortOrder:
          typeof rawOffering.sortOrder === 'number' && Number.isFinite(rawOffering.sortOrder)
            ? rawOffering.sortOrder
            : metadataEntry?.sortOrder ?? offeringMap.size,
        type: metadataEntry?.type ?? 'core',
        addOnEnabled: metadataEntry?.addOnEnabled ?? false,
        unitPriceCents: metadataEntry?.unitPriceCents ?? null,
        sla: metadataEntry?.sla ?? DEFAULT_OFFERING_SLA,
        subcategories: metadataEntry?.subcategories ?? [],
      });
    }
  }

  if (offeringMap.size > 0) {
    return Array.from(offeringMap.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    );
  }

  const offeringsCatalog = Array.isArray(service.metadata?.offeringsCatalog)
    ? service.metadata.offeringsCatalog
    : [];

  return offeringsCatalog
    .map((rawEntry, index) => {
      const entry = asRecord(rawEntry);
      const code = typeof entry.code === 'string' ? entry.code.trim() : '';
      const name = typeof entry.name === 'string' ? entry.name.trim() : '';
      if (!code || !name) return null;
      return {
        code,
        name,
        description:
          typeof entry.description === 'string' ? entry.description.trim() : '',
        status:
          typeof entry.status === 'string' && entry.status.trim()
            ? entry.status.trim()
            : 'published',
        sortOrder:
          typeof entry.sortOrder === 'number' && Number.isFinite(entry.sortOrder)
            ? entry.sortOrder
            : index,
        type:
          typeof entry.type === 'string' && entry.type.trim()
            ? entry.type.trim()
            : 'core',
        addOnEnabled: Boolean(entry.addOnEnabled),
        unitPriceCents:
          typeof entry.unitPriceCents === 'number' && Number.isFinite(entry.unitPriceCents)
            ? entry.unitPriceCents
            : null,
        sla: asRecord(entry.sla),
        subcategories: readOfferingSubcategories(entry.subcategories, code),
      } satisfies ServiceOfferingCatalogEntry;
    })
    .filter((entry): entry is ServiceOfferingCatalogEntry => Boolean(entry))
    .filter((entry) => normalizeText(entry.status) !== 'archived')
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
};

const initials = (name: string) => name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('');

export default function TeamsKnowledgeBasePage() {
  const [processes, setProcesses] = React.useState<KnowledgeProcess[]>([]);
  const [processesLoading, setProcessesLoading] = React.useState(true);
  const [processesError, setProcessesError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [serviceFilter, setServiceFilter] = React.useState<'all' | string>('all');
  const [statusFilter, setStatusFilter] = React.useState<'all' | ProcessStatus>('all');
  const [processTypeFilter, setProcessTypeFilter] = React.useState<ProcessTypeFilter>(DEFAULT_PROCESS_TYPE_FILTER);
  const [bookmarkFilter, setBookmarkFilter] = React.useState<BookmarkFilter>(DEFAULT_BOOKMARK_FILTER);
  const [sortMode, setSortMode] = React.useState<SortMode>('recent');
  const [page, setPage] = React.useState(1);
  const [openTabs, setOpenTabs] = React.useState<string[]>([]);
  const [activeTabId, setActiveTabId] = React.useState('');
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftProcess, setDraftProcess] = React.useState<KnowledgeProcess | null>(null);
  const [draftIds, setDraftIds] = React.useState<string[]>([]);
  const [catalogServices, setCatalogServices] = React.useState<CatalogServiceDefinition[]>([]);
  const [customCategories, setCustomCategories] = React.useState<CustomKnowledgeCategory[]>([]);
  const [categorySubcategories, setCategorySubcategories] = React.useState<Record<string, string[]>>({});
  const [newSubcategory, setNewSubcategory] = React.useState('');
  const [newTag, setNewTag] = React.useState('');
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [deleteModalProcessId, setDeleteModalProcessId] = React.useState<string | null>(null);
  const [deleteConfirmationValue, setDeleteConfirmationValue] = React.useState('');
  const [statusMenuOpen, setStatusMenuOpen] = React.useState(false);
  const [actionMenuOpen, setActionMenuOpen] = React.useState(false);
  const [inspectorTab, setInspectorTab] = React.useState<InspectorTabKey>('details');
  const [createDraft, setCreateDraft] = React.useState<CreateProcessDraft>(
    createInitialCreateProcessDraft(),
  );
  const [filterDraft, setFilterDraft] = React.useState<{
    service: 'all' | string;
    status: 'all' | ProcessStatus;
    processType: ProcessTypeFilter;
    bookmark: BookmarkFilter;
    sort: SortMode;
  }>({
    service: 'all',
    status: 'all',
    processType: DEFAULT_PROCESS_TYPE_FILTER,
    bookmark: DEFAULT_BOOKMARK_FILTER,
    sort: 'recent',
  });
  const deferredSearch = React.useDeferredValue(search);
  const editorRef = React.useRef<HTMLDivElement | null>(null);
  const statusMenuRef = React.useRef<HTMLDivElement | null>(null);
  const actionMenuRef = React.useRef<HTMLDivElement | null>(null);

  const loadProcesses = React.useCallback(async () => {
    setProcessesLoading(true);
    setProcessesError('');

    try {
      const response = await apiFetch<KBaseProcessesResponse>('/api/admin/kbase/processes');
      const nextProcesses = Array.isArray(response.processes) ? response.processes : [];
      setProcesses(nextProcesses);
    } catch (error) {
      setProcesses([]);
      setProcessesError(
        error instanceof Error ? error.message : 'Unable to load knowledge base processes right now.',
      );
    } finally {
      setProcessesLoading(false);
    }
  }, []);

  const loadCatalogServices = React.useCallback(async () => {
    const response = await apiFetch<PublicCatalogServicesResponse>('/api/catalog/services');
    setCatalogServices(Array.isArray(response.services) ? response.services : []);
  }, []);

  React.useEffect(() => {
    void loadProcesses();
  }, [loadProcesses]);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const response = await apiFetch<PublicCatalogServicesResponse>('/api/catalog/services');
        if (cancelled) return;
        setCatalogServices(Array.isArray(response.services) ? response.services : []);
      } catch {
        if (!cancelled) {
          setCatalogServices([]);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const serviceOptions = React.useMemo(
    () =>
      [...catalogServices]
        .map((service) => ({
          id: service.id,
          code: String(service.category).trim(),
          label: readServiceLabel(service),
        }))
        .filter((service) => service.code && service.label)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catalogServices],
  );

  const serviceOptionByCode = React.useMemo(
    () => new Map(serviceOptions.map((option) => [option.code, option] as const)),
    [serviceOptions],
  );
  const serviceByCode = React.useMemo(
    () => new Map(catalogServices.map((service) => [String(service.category).trim(), service] as const)),
    [catalogServices],
  );
  const serviceFilterOptions = React.useMemo<GlassPicklistOption<string>[]>(
    () => [
      { value: 'all', label: 'All services' },
      ...serviceOptions.map((option) => ({ value: option.code, label: option.label })),
    ],
    [serviceOptions],
  );
  const createServiceOptions = React.useMemo<GlassPicklistOption<string>[]>(
    () => serviceOptions.map((option) => ({ value: option.code, label: option.label })),
    [serviceOptions],
  );
  const statusFilterOptions = React.useMemo<GlassPicklistOption<'all' | ProcessStatus>[]>(
    () => [
      { value: 'all', label: 'All statuses' },
      { value: 'Verified', label: 'Verified' },
      { value: 'Draft', label: 'Draft' },
      { value: 'Needs Review', label: 'Needs review' },
    ],
    [],
  );
  const processTypeFilterOptions = React.useMemo<GlassPicklistOption<ProcessTypeFilter>[]>(
    () => [
      { value: 'all', label: 'All process types' },
      { value: 'service', label: 'Service processes' },
      { value: 'internal', label: 'Internal processes' },
    ],
    [],
  );
  const bookmarkFilterOptions = React.useMemo<GlassPicklistOption<BookmarkFilter>[]>(
    () => [
      { value: 'all', label: 'All bookmark states' },
      { value: 'bookmarked', label: 'Bookmarked only' },
    ],
    [],
  );
  const sortModeOptions = React.useMemo<GlassPicklistOption<SortMode>[]>(
    () => [
      { value: 'recent', label: 'Most recent' },
      { value: 'title', label: 'Title A-Z' },
      { value: 'service', label: 'Service' },
    ],
    [],
  );

  const getCategoryOptionsForService = React.useCallback(
    (serviceCode: string | null): KnowledgeCategoryOption[] => {
      const normalizedServiceCode = (serviceCode ?? '').trim();
      const options: KnowledgeCategoryOption[] = [];
      const seen = new Set<string>();

      for (const service of catalogServices) {
        const catalogServiceCode = String(service.category).trim();
        if (!normalizedServiceCode || catalogServiceCode !== normalizedServiceCode) continue;

        const offerings = readServiceOfferingCatalog(service);

        for (const offering of offerings) {
          const normalizedLabel = normalizeText(offering.name);
          if (!normalizedLabel || seen.has(normalizedLabel)) continue;
          seen.add(normalizedLabel);
          options.push({
            label: offering.name,
            normalizedLabel,
            source: 'service',
            serviceCode: catalogServiceCode,
            serviceTitle: readServiceLabel(service),
            offeringCode: offering.code,
          });
        }
      }

      for (const category of customCategories) {
        if ((category.serviceCode ?? '') !== normalizedServiceCode) continue;
        const normalizedLabel = normalizeText(category.label);
        if (!normalizedLabel || seen.has(normalizedLabel)) continue;
        seen.add(normalizedLabel);
        options.push({
          label: category.label,
          normalizedLabel,
          source: 'custom',
          serviceCode: category.serviceCode,
          serviceTitle: category.serviceLabel,
          offeringCode: null,
        });
      }

      return options.sort((a, b) => {
        if (a.source !== b.source) return a.source === 'service' ? -1 : 1;
        if (a.serviceTitle && b.serviceTitle && a.serviceTitle !== b.serviceTitle) {
          return a.serviceTitle.localeCompare(b.serviceTitle);
        }
        return a.label.localeCompare(b.label);
      });
    },
    [catalogServices, customCategories],
  );

  const filtered = React.useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    const next = processes.filter((process) => {
      if (serviceFilter !== 'all' && process.serviceCode !== serviceFilter) return false;
      if (statusFilter !== 'all' && process.status !== statusFilter) return false;
      if (processTypeFilter !== 'all' && process.processType !== processTypeFilter) return false;
      if (bookmarkFilter === 'bookmarked' && !process.isBookmarked) return false;
      if (!query) return true;
      return [
        process.title,
        process.serviceLabel ?? '',
        process.category,
        process.summary,
        ...process.subcategories,
        ...process.tags,
      ].join(' ').toLowerCase().includes(query);
    });
    return [...next].sort((a, b) => {
      if (sortMode === 'title') return a.title.localeCompare(b.title);
      if (sortMode === 'service') {
        return (a.serviceLabel ?? '').localeCompare(b.serviceLabel ?? '') || a.title.localeCompare(b.title);
      }
      return (b.revisions[0]?.changedAt ?? '').localeCompare(a.revisions[0]?.changedAt ?? '');
    });
  }, [
    bookmarkFilter,
    deferredSearch,
    processes,
    processTypeFilter,
    serviceFilter,
    sortMode,
    statusFilter,
  ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  React.useEffect(
    () => setPage(1),
    [bookmarkFilter, deferredSearch, processTypeFilter, serviceFilter, sortMode, statusFilter],
  );
  React.useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const active = processes.find((process) => process.id === activeTabId) ?? null;
  const currentProcess = isEditing && draftProcess?.id === active?.id ? draftProcess : active;
  const openRecords = openTabs.map((id) => processes.find((process) => process.id === id)).filter(Boolean) as KnowledgeProcess[];
  const linked = currentProcess ? currentProcess.linkedIds.map((id) => processes.find((process) => process.id === id)).filter(Boolean) as KnowledgeProcess[] : [];
  const pendingDeleteProcess = React.useMemo(
    () => (deleteModalProcessId ? processes.find((process) => process.id === deleteModalProcessId) ?? null : null),
    [deleteModalProcessId, processes],
  );
  const deleteConfirmationPhrase = pendingDeleteProcess
    ? `delete ${pendingDeleteProcess.title}`
    : '';
  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (serviceFilter !== 'all') count += 1;
    if (statusFilter !== 'all') count += 1;
    if (processTypeFilter !== DEFAULT_PROCESS_TYPE_FILTER) count += 1;
    if (bookmarkFilter !== DEFAULT_BOOKMARK_FILTER) count += 1;
    if (sortMode !== 'recent') count += 1;
    return count;
  }, [bookmarkFilter, processTypeFilter, serviceFilter, sortMode, statusFilter]);
  const latestBodyHtmlRef = React.useRef('');

  React.useEffect(() => {
    latestBodyHtmlRef.current = currentProcess?.bodyHtml ?? '';
  }, [currentProcess?.bodyHtml]);

  React.useEffect(() => {
    setInspectorTab('details');
  }, [activeTabId]);

  const openFilterModal = React.useCallback(() => {
    setFilterDraft({
      service: serviceFilter,
      status: statusFilter,
      processType: processTypeFilter,
      bookmark: bookmarkFilter,
      sort: sortMode,
    });
    setFilterModalOpen(true);
  }, [bookmarkFilter, processTypeFilter, serviceFilter, sortMode, statusFilter]);

  const closeFilterModal = React.useCallback(() => {
    setFilterModalOpen(false);
  }, []);

  const resetFilterDraft = React.useCallback(() => {
    setFilterDraft({
      service: 'all',
      status: 'all',
      processType: DEFAULT_PROCESS_TYPE_FILTER,
      bookmark: DEFAULT_BOOKMARK_FILTER,
      sort: 'recent',
    });
  }, []);

  const applyFilterDraft = React.useCallback(() => {
    setServiceFilter(filterDraft.service);
    setStatusFilter(filterDraft.status);
    setProcessTypeFilter(filterDraft.processType);
    setBookmarkFilter(filterDraft.bookmark);
    setSortMode(filterDraft.sort);
    setFilterModalOpen(false);
  }, [filterDraft]);

  React.useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = latestBodyHtmlRef.current;
  }, [currentProcess?.id, isEditing]);

  React.useEffect(() => {
    const validIds = new Set(processes.map((process) => process.id));

    setOpenTabs((current) => {
      const filtered = current.filter((id) => validIds.has(id));
      if (filtered.length === current.length && filtered.every((id, index) => id === current[index])) {
        return current;
      }
      if (filtered.length) return filtered;
      return processes[0] ? [processes[0].id] : [];
    });

    setActiveTabId((current) => {
      if (current && validIds.has(current)) return current;
      return processes[0]?.id ?? '';
    });

    if (!processes.length) {
      setIsEditing(false);
      setDraftProcess(null);
    }
  }, [processes]);

  const openProcess = (id: string) => {
    setIsEditing(false);
    setDraftProcess(null);
    setOpenTabs((current) => (current.includes(id) ? current : [...current, id]));
    setActiveTabId(id);
  };

  const closeTab = React.useCallback((id: string) => {
    setOpenTabs((current) => {
      const next = current.filter((entry) => entry !== id);
      if (!next.length) {
        setActiveTabId('');
        setIsEditing(false);
        setDraftProcess(null);
        return [];
      }
      if (activeTabId === id) {
        setActiveTabId(next[next.length - 1]);
        setIsEditing(false);
        setDraftProcess(null);
      }
      return next;
    });
  }, [activeTabId]);

  const currentEditorServiceCode =
    (draftProcess?.serviceCode ?? currentProcess?.serviceCode ?? '').trim() || null;
  const currentEditorCategoryLabel =
    (draftProcess?.category ?? currentProcess?.category ?? '').trim();
  const availableCategoryOptions = React.useMemo(
    () => getCategoryOptionsForService(currentEditorServiceCode),
    [currentEditorServiceCode, getCategoryOptionsForService],
  );
  const editorServiceOptions = React.useMemo<GlassPicklistOption<string>[]>(
    () => [
      { value: '', label: 'No linked service' },
      ...serviceOptions.map((option) => ({ value: option.code, label: option.label })),
    ],
    [serviceOptions],
  );
  const editorStatusOptions = React.useMemo<readonly ProcessStatus[]>(
    () => ['Verified', 'Draft', 'Needs Review'],
    [],
  );
  const categoryOptionByLabel = React.useMemo(
    () =>
      new Map(
        availableCategoryOptions.map((option) => [option.normalizedLabel, option] as const),
      ),
    [availableCategoryOptions],
  );
  const selectedCategoryOption =
    currentEditorCategoryLabel
      ? categoryOptionByLabel.get(normalizeText(currentEditorCategoryLabel)) ?? null
      : null;
  const editorCategoryOptions = React.useMemo<GlassPicklistOption<string>[]>(
    () => {
      const options: GlassPicklistOption<string>[] = [
        { value: '', label: 'Select category' },
        ...availableCategoryOptions.map((option) => ({
          value: option.label,
          label: option.serviceTitle
            ? `${option.label} - ${option.serviceTitle}`
            : option.label,
        })),
      ];
      if (
        currentEditorCategoryLabel
        && !availableCategoryOptions.some(
          (option) => option.normalizedLabel === normalizeText(currentEditorCategoryLabel),
        )
      ) {
        options.push({
          value: currentEditorCategoryLabel,
          label: `${currentEditorCategoryLabel} (Custom)`,
        });
      }
      return options;
    },
    [availableCategoryOptions, currentEditorCategoryLabel],
  );
  const availableSubcategories = React.useMemo(() => {
    if (!currentEditorCategoryLabel) return [];
    const registryValues =
      categorySubcategories[
        makeCategoryRegistryKey(currentEditorServiceCode, currentEditorCategoryLabel)
      ] ?? [];
    if (registryValues.length) return registryValues;
    const currentService = currentEditorServiceCode
      ? serviceByCode.get(currentEditorServiceCode) ?? null
      : null;
    if (!currentService) return [];
    const matchingOffering = readServiceOfferingCatalog(currentService).find(
      (offering) => normalizeText(offering.name) === normalizeText(currentEditorCategoryLabel),
    );
    return matchingOffering?.subcategories.map((subcategory) => subcategory.name) ?? [];
  }, [categorySubcategories, currentEditorCategoryLabel, currentEditorServiceCode, serviceByCode]);
  const currentEditorOffering = React.useMemo(() => {
    if (!currentEditorServiceCode) return null;
    const currentService = serviceByCode.get(currentEditorServiceCode) ?? null;
    if (!currentService) return null;
    const activeOfferingCode =
      (draftProcess?.offeringCode ?? currentProcess?.offeringCode ?? selectedCategoryOption?.offeringCode ?? '').trim()
      || null;
    return (
      readServiceOfferingCatalog(currentService).find((offering) =>
        activeOfferingCode
          ? offering.code === activeOfferingCode
          : normalizeText(offering.name) === normalizeText(currentEditorCategoryLabel),
      ) ?? null
    );
  }, [
    currentEditorCategoryLabel,
    currentEditorServiceCode,
    currentProcess?.offeringCode,
    draftProcess?.offeringCode,
    selectedCategoryOption?.offeringCode,
    serviceByCode,
  ]);
  const lockedServiceSubcategoryName = React.useMemo(() => {
    if ((draftProcess?.processType ?? currentProcess?.processType ?? 'internal') !== 'service') {
      return null;
    }
    const selectedSubcategoryId =
      (draftProcess?.subcategoryId ?? currentProcess?.subcategoryId ?? '').trim() || null;
    if (!selectedSubcategoryId || !currentEditorOffering) return null;
    return (
      currentEditorOffering.subcategories.find(
        (subcategory) => subcategory.id === selectedSubcategoryId,
      )?.name ?? null
    );
  }, [
    currentEditorOffering,
    currentProcess?.subcategoryId,
    draftProcess?.subcategoryId,
    currentProcess?.processType,
    draftProcess?.processType,
  ]);

  const createSelectedService = React.useMemo(
    () => serviceByCode.get(createDraft.serviceCode.trim()) ?? null,
    [createDraft.serviceCode, serviceByCode],
  );
  const createServiceOfferings = React.useMemo(
    () => (createSelectedService ? readServiceOfferingCatalog(createSelectedService) : []),
    [createSelectedService],
  );
  const createOfferingOptions = React.useMemo<GlassPicklistOption<string>[]>(
    () =>
      createServiceOfferings.map((offering) => ({
        value: offering.code,
        label: offering.name,
      })),
    [createServiceOfferings],
  );
  const createSelectedOffering = React.useMemo(
    () =>
      createServiceOfferings.find((offering) => offering.code === createDraft.offeringCode) ?? null,
    [createDraft.offeringCode, createServiceOfferings],
  );
  const createSubcategoryPool = React.useMemo(
    () => createSelectedOffering?.subcategories ?? [],
    [createSelectedOffering],
  );
  const createSubcategoryOptions = React.useMemo<GlassPicklistOption<string>[]>(
    () =>
      createSubcategoryPool.map((subcategory) => ({
        value: subcategory.id,
        label: subcategory.name,
      })),
    [createSubcategoryPool],
  );
  const existingTagPool = React.useMemo(
    () =>
      dedupeStrings(
        processes.flatMap((process) => process.tags),
      ).sort((a, b) => a.localeCompare(b)),
    [processes],
  );
  const filteredTagSuggestions = React.useMemo(() => {
    const query = normalizeText(createDraft.tagQuery);
    return existingTagPool.filter((tag) => {
      if (createDraft.tags.some((selected) => normalizeText(selected) === normalizeText(tag))) {
        return false;
      }
      if (!query) return true;
      return normalizeText(tag).includes(query);
    });
  }, [createDraft.tagQuery, createDraft.tags, existingTagPool]);

  const mergeSubcategoriesForCategory = React.useCallback(
    (serviceCode: string | null, categoryLabel: string, values: readonly string[]) => {
      const normalizedServiceCode = (serviceCode ?? '').trim() || null;
      const normalizedCategoryLabel = categoryLabel.trim();
      if (!normalizedCategoryLabel) return;

      setCategorySubcategories((current) => {
        const registryKey = makeCategoryRegistryKey(
          normalizedServiceCode,
          normalizedCategoryLabel,
        );
        return {
          ...current,
          [registryKey]: dedupeStrings([
            ...(current[registryKey] ?? []),
            ...values,
          ]),
        };
      });
    },
    [],
  );

  const registerCustomCategory = React.useCallback(
    (serviceCode: string | null, serviceLabel: string | null, categoryLabel: string) => {
      const normalizedServiceCode = (serviceCode ?? '').trim() || null;
      const normalizedCategoryLabel = categoryLabel.trim();
      if (!normalizedCategoryLabel) return;

      setCustomCategories((current) => {
        const alreadyExists = current.some(
          (entry) =>
            (entry.serviceCode ?? null) === normalizedServiceCode
            && normalizeText(entry.label) === normalizeText(normalizedCategoryLabel),
        );
        if (alreadyExists) return current;
        return [
          ...current,
          {
            serviceCode: normalizedServiceCode,
            serviceLabel,
            label: normalizedCategoryLabel,
          },
        ];
      });
    },
    [],
  );

  const registerCurrentCategory = React.useCallback(() => {
    if (!draftProcess) return;
    const categoryLabel = draftProcess.category.trim();
    if (!categoryLabel) return;

    const matchingExistingOption = getCategoryOptionsForService(draftProcess.serviceCode).find(
      (option) => option.normalizedLabel === normalizeText(categoryLabel),
    );

    if (matchingExistingOption) {
      updateDraft((current) => ({
        ...current,
        category: matchingExistingOption.label,
        serviceCode: matchingExistingOption.serviceCode ?? current.serviceCode,
        serviceLabel: matchingExistingOption.serviceTitle ?? current.serviceLabel,
      }));
      return;
    }

    registerCustomCategory(draftProcess.serviceCode, draftProcess.serviceLabel, categoryLabel);
  }, [draftProcess, getCategoryOptionsForService, registerCustomCategory]);

  const addCustomSubcategory = React.useCallback(() => {
    if (!draftProcess) return;
    const nextSubcategory = newSubcategory.trim();
    if (!draftProcess.category.trim() || !nextSubcategory) {
      return;
    }

    mergeSubcategoriesForCategory(
      draftProcess.serviceCode,
      draftProcess.category,
      [nextSubcategory],
    );
    updateDraft((current) => ({
      ...current,
      subcategories: dedupeStrings([...current.subcategories, nextSubcategory]),
    }));
    setNewSubcategory('');
  }, [draftProcess, mergeSubcategoriesForCategory, newSubcategory]);

  const addCustomTag = React.useCallback(() => {
    const nextTag = newTag.trim();
    if (!nextTag) return;
    updateDraft((current) => ({
      ...current,
      tags: dedupeStrings([...current.tags, nextTag]),
    }));
    setNewTag('');
  }, [newTag]);

  React.useEffect(() => {
    setNewSubcategory('');
  }, [currentEditorCategoryLabel, currentEditorServiceCode]);

  const updateCreateDraft = React.useCallback(
    (updater: (current: CreateProcessDraft) => CreateProcessDraft) => {
      setCreateDraft((current) => updater(current));
    },
    [],
  );

  const openCreateModal = React.useCallback(() => {
    setCreateDraft(createInitialCreateProcessDraft());
    setCreateModalOpen(true);
  }, []);

  const closeCreateModal = React.useCallback(() => {
    setCreateModalOpen(false);
    setCreateDraft(createInitialCreateProcessDraft());
  }, []);

  React.useEffect(() => {
    if (!createModalOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeCreateModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeCreateModal, createModalOpen]);

  React.useEffect(() => {
    if (!statusMenuOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && !statusMenuRef.current?.contains(target)) {
        setStatusMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [statusMenuOpen]);

  React.useEffect(() => {
    if (!actionMenuOpen) return undefined;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target && !actionMenuRef.current?.contains(target)) {
        setActionMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActionMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [actionMenuOpen]);

  React.useEffect(() => {
    setActionMenuOpen(false);
  }, [activeTabId, isEditing]);

  React.useEffect(() => {
    if (!deleteModalProcessId) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDeleteModalProcessId(null);
        setDeleteConfirmationValue('');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteModalProcessId]);

  React.useEffect(() => {
    if (!deleteModalProcessId) return;
    if (pendingDeleteProcess) return;
    setDeleteModalProcessId(null);
    setDeleteConfirmationValue('');
  }, [deleteModalProcessId, pendingDeleteProcess]);

  const addCreateTag = React.useCallback(() => {
    const nextTag = createDraft.tagQuery.trim();
    if (!nextTag) return;
    const matchingExistingTag = existingTagPool.find(
      (tag) => normalizeText(tag) === normalizeText(nextTag),
    );
    updateCreateDraft((current) => ({
      ...current,
      tags: dedupeStrings([...current.tags, matchingExistingTag ?? nextTag]),
      tagQuery: '',
      error: '',
    }));
  }, [createDraft.tagQuery, existingTagPool, updateCreateDraft]);

  const removeCreateTag = React.useCallback(
    (tag: string) => {
      updateCreateDraft((current) => ({
        ...current,
      tags: current.tags.filter((value) => normalizeText(value) !== normalizeText(tag)),
      }));
    },
    [updateCreateDraft],
  );

  const buildProcessPayload = React.useCallback(
    (process: KnowledgeProcess, revisionSummary?: string) => ({
      processType: process.processType,
      serviceId: process.serviceId,
      serviceCode: process.serviceCode,
      serviceLabel: process.serviceLabel,
      offeringCode: process.offeringCode,
      title: process.title,
      category: process.category,
      subcategoryId: process.subcategoryId,
      subcategories: process.subcategories,
      tags: process.tags,
      status: process.status,
      summary: process.summary,
      objective: process.objective,
      scope: process.scope,
      prerequisites: process.prerequisites,
      requiredInputs: process.requiredInputs,
      expectedOutputs: process.expectedOutputs,
      toolsAndPlatforms: process.toolsAndPlatforms,
      orderedSteps: process.orderedSteps,
      qaChecklist: process.qaChecklist,
      definitionOfDone: process.definitionOfDone,
      hardRules: process.hardRules,
      doNotDo: process.doNotDo,
      approvalRequirements: process.approvalRequirements,
      escalationTriggers: process.escalationTriggers,
      handoffRules: process.handoffRules,
      riskNotes: process.riskNotes,
      bodyHtml: process.bodyHtml,
      linkedIds: process.linkedIds,
      revisionSummary,
    }),
    [],
  );

  const selectCreateSubcategory = React.useCallback(
    (subcategory: CatalogServiceOfferingSubcategoryDefinition) => {
      updateCreateDraft((current) => ({
        ...current,
        selectedSubcategoryId: subcategory.id,
        selectedSubcategoryName: subcategory.name,
        selectedSubcategoryDescription: subcategory.description ?? '',
        subcategoryNameDraft: '',
        subcategoryDescriptionDraft: '',
        showSubcategoryComposer: false,
        error: '',
      }));
    },
    [updateCreateDraft],
  );

  const createSubcategoryFromDraft = React.useCallback(async () => {
    const service = createSelectedService;
    const offering = createSelectedOffering;
    const nextName = createDraft.subcategoryNameDraft.trim();
    const nextDescription = createDraft.subcategoryDescriptionDraft.trim();

    if (!service || !offering || !nextName) {
      updateCreateDraft((current) => ({
        ...current,
        error: 'Select a service and offering before creating a subcategory.',
      }));
      return;
    }

    const existing = offering.subcategories.find(
      (subcategory) => normalizeText(subcategory.name) === normalizeText(nextName),
    );
    if (existing) {
      selectCreateSubcategory(existing);
      return;
    }

    updateCreateDraft((current) => ({
      ...current,
      saving: true,
      error: '',
    }));

    try {
      const nextSubcategory: CatalogServiceOfferingSubcategoryDefinition = {
        id: makeSubcategoryId(offering.code, nextName),
        name: nextName,
        description: nextDescription,
      };
      const nextCatalog = readServiceOfferingCatalog(service).map((entry) =>
        entry.code === offering.code
          ? {
              ...entry,
              subcategories: dedupeStrings([
                ...entry.subcategories.map((subcategory) => subcategory.name),
                nextName,
              ]).map((name) => {
                const existingSubcategory = entry.subcategories.find(
                  (subcategory) => normalizeText(subcategory.name) === normalizeText(name),
                );
                if (existingSubcategory) return existingSubcategory;
                return nextSubcategory;
              }),
            }
          : entry,
      );

      await apiFetch(`/api/admin/services/${service.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          metadata: {
            ...(service.metadata ?? {}),
            offeringsCatalogVersion: 2,
            offeringsCatalog: nextCatalog,
          },
        }),
      });
      await loadCatalogServices();
      updateCreateDraft((current) => ({
        ...current,
        saving: false,
        selectedSubcategoryId: nextSubcategory.id,
        selectedSubcategoryName: nextSubcategory.name,
        selectedSubcategoryDescription: nextSubcategory.description ?? '',
        subcategoryNameDraft: '',
        subcategoryDescriptionDraft: '',
        showSubcategoryComposer: false,
        error: '',
      }));
    } catch (error) {
      updateCreateDraft((current) => ({
        ...current,
        saving: false,
        error:
          error instanceof Error
            ? error.message
            : 'Unable to create that subcategory right now.',
      }));
    }
  }, [
    createDraft.subcategoryDescriptionDraft,
    createDraft.subcategoryNameDraft,
    createSelectedOffering,
    createSelectedService,
    loadCatalogServices,
    selectCreateSubcategory,
    updateCreateDraft,
  ]);

  const startCreate = React.useCallback(async () => {
    const trimmedTitle = createDraft.title.trim();
    const processType = createDraft.type;

    if (!processType) {
      updateCreateDraft((current) => ({
        ...current,
        error: 'Choose whether this is a service process or an internal process.',
      }));
      return;
    }

    if (!trimmedTitle) {
      updateCreateDraft((current) => ({
        ...current,
        error: 'Name the process before creating it.',
      }));
      return;
    }

    if (processType === 'service') {
      const selectedService = createSelectedService;
      const selectedOffering = createSelectedOffering;
      if (!selectedService || !selectedOffering || !createDraft.selectedSubcategoryId) {
        updateCreateDraft((current) => ({
          ...current,
          error: 'Select a service, offering, and subcategory to create a service process.',
        }));
        return;
      }
    }

    if (processType === 'internal' && createDraft.tags.length < 1) {
      updateCreateDraft((current) => ({
        ...current,
        error: 'Internal processes require at least one tag.',
      }));
      return;
    }

    const selectedService = createSelectedService;
    const selectedOffering = createSelectedOffering;
    const selectedSubcategory =
      processType === 'service'
        ? createSubcategoryPool.find(
            (subcategory) => subcategory.id === createDraft.selectedSubcategoryId,
          ) ?? null
        : null;
    const defaultCategoryLabel =
      processType === 'service' && selectedOffering
        ? selectedOffering.name
        : 'Internal Process';
    const nextProcessDraft: KnowledgeProcess = {
      id: `draft-${Date.now()}`,
      processType,
      serviceId: processType === 'service' ? selectedService?.id ?? null : null,
      title: trimmedTitle,
      serviceCode: processType === 'service' ? selectedService ? String(selectedService.category).trim() : null : null,
      serviceLabel: processType === 'service' ? selectedService ? readServiceLabel(selectedService) : null : null,
      offeringCode: processType === 'service' ? selectedOffering?.code ?? null : null,
      category: defaultCategoryLabel,
      subcategoryId: processType === 'service' ? createDraft.selectedSubcategoryId : null,
      subcategories:
        processType === 'service' && selectedSubcategory?.name
          ? [selectedSubcategory.name]
          : [],
      tags: processType === 'internal' ? dedupeStrings(createDraft.tags) : [],
      status: 'Draft',
      ...DEFAULT_EXECUTION_FIELDS,
      ...DEFAULT_GUARDRAIL_FIELDS,
      objective:
        processType === 'service'
          ? `Complete ${selectedOffering?.name ?? 'service'} work for ${selectedSubcategory?.name ?? createDraft.selectedSubcategoryName}.`
          : `Standardize the internal workflow for ${trimmedTitle}.`,
      scope:
        processType === 'service'
          ? `Use this process for tickets mapped to ${selectedOffering?.name ?? 'this offering'} and ${selectedSubcategory?.name ?? createDraft.selectedSubcategoryName}.`
          : 'Use this process for internal operations that are not tied to a specific service ticket.',
      summary:
        processType === 'service'
          ? `Document the ${selectedOffering?.name ?? 'service'} process for ${selectedSubcategory?.name ?? createDraft.selectedSubcategoryName}.`
          : 'Document the internal process here and describe when the team should use it.',
      bodyHtml:
        processType === 'service'
          ? `<h1>${trimmedTitle}</h1><p>Document the ${selectedOffering?.name ?? 'service'} workflow for ${selectedSubcategory?.name ?? createDraft.selectedSubcategoryName}.</p>`
          : `<h1>${trimmedTitle}</h1><p>Document the internal process here.</p>`,
      linkedIds: [],
      isBookmarked: false,
      contributors: [],
      revisions: [],
    };

    updateCreateDraft((current) => ({
      ...current,
      saving: true,
      error: '',
    }));

    try {
      const createdProcess = await apiFetch<KnowledgeProcess>('/api/admin/kbase/processes', {
        method: 'POST',
        body: JSON.stringify(
          buildProcessPayload(nextProcessDraft, 'Created the initial process draft.'),
        ),
      });

      setProcesses((current) => [createdProcess, ...current]);
      setDraftIds((current) => [...current, createdProcess.id]);
      setOpenTabs((current) =>
        current.includes(createdProcess.id) ? current : [...current, createdProcess.id],
      );
      setActiveTabId(createdProcess.id);
      setDraftProcess(createdProcess);
      setIsEditing(true);
      closeCreateModal();
    } catch (error) {
      updateCreateDraft((current) => ({
        ...current,
        saving: false,
        error:
          error instanceof Error
            ? error.message
            : 'Unable to create that process right now.',
      }));
    }
  }, [
    buildProcessPayload,
    closeCreateModal,
    createDraft.selectedSubcategoryId,
    createDraft.selectedSubcategoryName,
    createDraft.tags,
    createDraft.title,
    createDraft.type,
    createSubcategoryPool,
    createSelectedOffering,
    createSelectedService,
    updateCreateDraft,
  ]);

  const startEdit = () => {
    if (!active) return;
    setActionMenuOpen(false);
    setDraftProcess({ ...active });
    setInspectorTab('details');
    setIsEditing(true);
  };

  const deleteProcess = React.useCallback(
    async (processId: string) => {
      try {
        await apiFetch(`/api/admin/kbase/processes/${processId}`, {
          method: 'DELETE',
        });
        setProcesses((current) => current.filter((process) => process.id !== processId));
        setDraftIds((current) => current.filter((id) => id !== processId));
        closeTab(processId);
        setStatusMenuOpen(false);
        setActionMenuOpen(false);
      } catch (error) {
        window.alert(
          error instanceof Error ? error.message : 'Unable to delete this process right now.',
        );
      }
    },
    [closeTab],
  );

  const deleteCurrentProcess = React.useCallback(() => {
    if (!currentProcess) return;
    setActionMenuOpen(false);
    setDeleteConfirmationValue('');
    setDeleteModalProcessId(currentProcess.id);
  }, [currentProcess]);

  const closeDeleteModal = React.useCallback(() => {
    setDeleteModalProcessId(null);
    setDeleteConfirmationValue('');
  }, []);

  const confirmDeleteProcess = React.useCallback(() => {
    if (!pendingDeleteProcess) return;
    if (deleteConfirmationValue.trim() !== deleteConfirmationPhrase) return;
    const processId = pendingDeleteProcess.id;
    closeDeleteModal();
    void deleteProcess(processId);
  }, [
    closeDeleteModal,
    deleteConfirmationPhrase,
    deleteConfirmationValue,
    deleteProcess,
    pendingDeleteProcess,
  ]);

  const toggleBookmark = React.useCallback(async () => {
    if (!currentProcess) return;
    const nextBookmarked = !currentProcess.isBookmarked;

    try {
      await apiFetch(`/api/admin/kbase/processes/${currentProcess.id}/bookmark`, {
        method: nextBookmarked ? 'POST' : 'DELETE',
      });
      setProcesses((current) =>
        current.map((process) =>
          process.id === currentProcess.id
            ? { ...process, isBookmarked: nextBookmarked }
            : process,
        ),
      );
      setActionMenuOpen(false);
    } catch (error) {
      window.alert(
        error instanceof Error
          ? error.message
          : 'Unable to update that bookmark right now.',
      );
    }
  }, [currentProcess]);

  const saveEdit = async () => {
    if (!draftProcess) return;
    const nextBodyHtml = editorRef.current?.innerHTML?.trim() || draftProcess.bodyHtml;
    const normalizedCategoryLabel = draftProcess.category.trim() || 'New Category';
    const normalizedSubcategories = dedupeStrings(draftProcess.subcategories);
    const normalizedTags = dedupeStrings(draftProcess.tags);
    const canonicalServiceCode = draftProcess.serviceCode?.trim() || null;
    const canonicalServiceLabel =
      (canonicalServiceCode ? serviceOptionByCode.get(canonicalServiceCode)?.label : null)
      ?? draftProcess.serviceLabel
      ?? null;
    const canonicalCategoryOption =
      normalizedCategoryLabel
        ? getCategoryOptionsForService(canonicalServiceCode).find(
            (option) => option.normalizedLabel === normalizeText(normalizedCategoryLabel),
          ) ?? null
        : null;
    const canonicalService = canonicalServiceCode
      ? serviceByCode.get(canonicalServiceCode) ?? null
      : null;
    const canonicalOffering = canonicalService
      ? readServiceOfferingCatalog(canonicalService).find((offering) =>
          canonicalCategoryOption?.offeringCode
            ? offering.code === canonicalCategoryOption.offeringCode
            : normalizeText(offering.name) === normalizeText(normalizedCategoryLabel),
        ) ?? null
      : null;
    const canonicalSubcategory =
      draftProcess.processType === 'service' && normalizedSubcategories.length === 1
        ? canonicalOffering?.subcategories.find(
            (subcategory) =>
              normalizeText(subcategory.name) === normalizeText(normalizedSubcategories[0]),
          ) ?? null
        : null;
    const nextProcessDraft: KnowledgeProcess = {
      ...draftProcess,
      serviceId:
        draftProcess.processType === 'service'
          ? canonicalService?.id ?? draftProcess.serviceId
          : null,
      serviceCode: canonicalCategoryOption?.serviceCode ?? canonicalServiceCode,
      serviceLabel: canonicalCategoryOption?.serviceTitle ?? canonicalServiceLabel,
      offeringCode:
        draftProcess.processType === 'service'
          ? canonicalCategoryOption?.offeringCode ?? draftProcess.offeringCode
          : null,
      category: canonicalCategoryOption?.label ?? normalizedCategoryLabel,
      subcategoryId:
        draftProcess.processType === 'service'
          ? canonicalSubcategory?.id ?? null
          : null,
      subcategories: normalizedSubcategories,
      tags: normalizedTags,
      bodyHtml: nextBodyHtml,
    };

    try {
      const savedProcess = await apiFetch<KnowledgeProcess>(
        `/api/admin/kbase/processes/${draftProcess.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(
            buildProcessPayload(
              nextProcessDraft,
              draftIds.includes(draftProcess.id)
                ? 'Created the initial process draft.'
                : 'Updated process content and metadata.',
            ),
          ),
        },
      );

      setProcesses((current) =>
        current.map((process) => (process.id === savedProcess.id ? savedProcess : process)),
      );
      setDraftIds((current) => current.filter((id) => id !== savedProcess.id));
      if (savedProcess.category.trim()) {
        mergeSubcategoriesForCategory(
          savedProcess.serviceCode,
          savedProcess.category,
          savedProcess.subcategories,
        );
      }
      setDraftProcess(null);
      setIsEditing(false);
    } catch (error) {
      window.alert(
        error instanceof Error ? error.message : 'Unable to save this process right now.',
      );
    }
  };

  const updateDraft = (updater: (current: KnowledgeProcess) => KnowledgeProcess) => {
    setDraftProcess((current) => (current ? updater(current) : current));
  };

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const editingProcessType = draftProcess?.processType ?? currentProcess?.processType ?? 'internal';
  const isLockedServiceProcess = editingProcessType === 'service';
  const currentEditableTags = dedupeStrings(draftProcess?.tags ?? currentProcess?.tags ?? []);
  const currentEditableSubcategories = lockedServiceSubcategoryName
    ? [lockedServiceSubcategoryName]
    : dedupeStrings(draftProcess?.subcategories ?? currentProcess?.subcategories ?? []);
  const updateDraftTextField = (key: 'objective' | 'scope', value: string) => {
    updateDraft((current) => ({
      ...current,
      [key]: value,
    }));
  };
  const updateDraftListField = (key: KnowledgeListFieldKey, value: string) => {
    updateDraft((current) => ({
      ...current,
      [key]: parseListField(value),
    }));
  };
  const renderReadOnlyList = (values: readonly string[], emptyLabel: string) => {
    if (!values.length) {
      return (
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', fontWeight: 700 }}>
          {emptyLabel}
        </div>
      );
    }
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {values.map((value, index) => (
          <div key={`${value}-${index}`} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 8, alignItems: 'start' }}>
            <span style={{ color: backendPalette.yellow, fontSize: '0.82rem', fontWeight: 900, lineHeight: 1.5 }}>
              {index + 1}.
            </span>
            <div style={{ color: 'rgba(255,255,255,0.76)', fontSize: '0.84rem', lineHeight: 1.6, fontWeight: 700 }}>
              {value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Page>
      <Header>
        <Eyebrow>Internal Process Tool</Eyebrow>
        <TitleRow>
          <Title>Knowledge Base</Title>
          <SmallButton type="button" $accent onClick={openCreateModal}>
            <Plus size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
            Create Process
          </SmallButton>
        </TitleRow>
        <Subtitle>
          Internal knowledge workspace for service and internal processes, with searchable
          discovery, multi-tab editing, linked knowledge, and revision visibility.
        </Subtitle>
      </Header>

      <Workspace>
        <LibraryPanel>
          <PanelHeader>
            <PanelTitle>Process Library</PanelTitle>
            <PanelCopy>Search, filter, and page through processes by service, category, tags, and status.</PanelCopy>
            {processesError ? (
              <div style={{ color: backendPalette.yellow, fontSize: '0.76rem', fontWeight: 800 }}>
                {processesError}
              </div>
            ) : null}
          </PanelHeader>
          <Toolbar>
            <SearchRow>
              <SearchWrap>
                <SearchIcon />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search processes, services, categories, subcategories, tags"
                />
              </SearchWrap>
              <FilterButton
                type="button"
                onClick={openFilterModal}
                aria-label="Open process filters"
                title="Open filters"
              >
                <Filter size={16} />
                {activeFilterCount ? (
                  <FilterCountBadge>{activeFilterCount}</FilterCountBadge>
                ) : null}
              </FilterButton>
            </SearchRow>
          </Toolbar>
          <LibraryBody>
            {processesLoading ? (
              <div
                style={{
                  border: '1px dashed rgba(255,255,255,0.18)',
                  borderRadius: 16,
                  padding: 18,
                  color: 'rgba(255,255,255,0.66)',
                  textAlign: 'center',
                  fontWeight: 700,
                }}
              >
                Loading knowledge base...
              </div>
            ) : visible.length ? visible.map((process) => (
              <ProcessButton key={process.id} type="button" $active={process.id === activeTabId} onClick={() => openProcess(process.id)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                    {process.isBookmarked ? (
                      <Bookmark
                        size={14}
                        color={backendPalette.yellow}
                        fill="rgba(224, 161, 94, 0.16)"
                        aria-label="Bookmarked process"
                      />
                    ) : null}
                    <div style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.95rem', fontWeight: 900, minWidth: 0 }}>{process.title}</div>
                  </div>
                  <div style={{ color: backendPalette.yellow, fontSize: '0.66rem', fontWeight: 900, textTransform: 'uppercase' }}>{process.status}</div>
                </div>
                {process.tags.length ? (
                  <ProcessTagRow>
                    {process.tags.slice(0, 2).map((tag) => (
                      <ProcessTagChip key={tag}>#{tag}</ProcessTagChip>
                    ))}
                    {process.tags.length > 2 ? (
                      <ProcessTagChip>+{process.tags.length - 2}</ProcessTagChip>
                    ) : null}
                  </ProcessTagRow>
                ) : null}
                <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.72rem', fontWeight: 700 }}>
                  {process.revisions[0]?.author ?? 'Unknown'} edited on {process.revisions[0]?.changedAt ?? '--'}
                </div>
              </ProcessButton>
            )) : (
              <div style={{ border: '1px dashed rgba(255,255,255,0.18)', borderRadius: 16, padding: 18, color: 'rgba(255,255,255,0.66)', textAlign: 'center', fontWeight: 700 }}>
                No processes match the current filters.
              </div>
            )}
          </LibraryBody>
          <Pagination>
            <div style={{ color: 'rgba(255,255,255,0.58)', fontSize: '0.76rem', fontWeight: 800, textTransform: 'uppercase' }}>Page {page} of {totalPages}</div>
            <div style={{ display: 'inline-flex', gap: 8 }}>
              <SmallButton type="button" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><ChevronLeft size={14} /></SmallButton>
              <SmallButton type="button" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}><ChevronRight size={14} /></SmallButton>
            </div>
          </Pagination>
        </LibraryPanel>

        <WorkspacePanel>
          <TabBar>
            {openRecords.length ? openRecords.map((process) => (
              <Tab key={process.id} type="button" $active={process.id === activeTabId} onClick={() => setActiveTabId(process.id)}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.82rem', fontWeight: 800 }}>{process.title}</span>
                <span
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 999, background: 'rgba(255,255,255,0.08)' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    closeTab(process.id);
                  }}
                >
                  <X size={12} />
                </span>
              </Tab>
            )) : <Tab type="button" $active><span style={{ fontSize: '0.82rem', fontWeight: 800 }}>No process open</span></Tab>}
          </TabBar>

          {currentProcess ? (
            <WorkspaceBody>
              <EditorColumn>
                <PanelHeader>
                  <PanelTitleRow>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      {isEditing ? (
                        <PlainInput
                          value={draftProcess?.title ?? currentProcess.title}
                          onChange={(event) =>
                            updateDraft((current) => ({
                              ...current,
                              title: event.target.value,
                            }))
                          }
                          placeholder="Process title"
                          style={{
                            minHeight: 46,
                            fontSize: '1.15rem',
                            fontWeight: 900,
                            paddingInline: 14,
                          }}
                        />
                      ) : (
                        <div style={{ color: 'rgba(255,255,255,0.97)', fontSize: 'clamp(1.35rem, 2vw, 1.95rem)', fontWeight: 900, lineHeight: 1.08 }}>
                          {currentProcess.title}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {isEditing ? (
                        <>
                          <StatusMenuWrap ref={statusMenuRef}>
                            <StatusMenuTrigger
                              type="button"
                              $open={statusMenuOpen}
                              onClick={() => setStatusMenuOpen((current) => !current)}
                              aria-haspopup="menu"
                              aria-expanded={statusMenuOpen}
                              aria-label="Select process status"
                            >
                              <span>{draftProcess?.status ?? currentProcess.status}</span>
                              <span>{statusMenuOpen ? '^' : 'v'}</span>
                            </StatusMenuTrigger>
                            {statusMenuOpen ? (
                              <StatusMenuPanel role="menu" aria-label="Process status options">
                                {editorStatusOptions.map((status) => (
                                  <StatusMenuOption
                                    key={status}
                                    type="button"
                                    role="menuitem"
                                    $selected={(draftProcess?.status ?? currentProcess.status) === status}
                                    onClick={() => {
                                      updateDraft((current) => ({
                                        ...current,
                                        status,
                                      }));
                                      setStatusMenuOpen(false);
                                    }}
                                  >
                                    {status}
                                  </StatusMenuOption>
                                ))}
                              </StatusMenuPanel>
                            ) : null}
                          </StatusMenuWrap>
                          <SmallButton type="button" $accent onClick={saveEdit}>
                            <Save size={14} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
                            Save
                          </SmallButton>
                        </>
                      ) : (
                        <ActionMenuWrap ref={actionMenuRef}>
                          <ActionMenuTrigger
                            type="button"
                            $open={actionMenuOpen}
                            onClick={() => setActionMenuOpen((current) => !current)}
                            aria-haspopup="menu"
                            aria-expanded={actionMenuOpen}
                            aria-label="Process actions"
                          >
                            <MoreHorizontal size={16} />
                          </ActionMenuTrigger>
                          {actionMenuOpen ? (
                            <StatusMenuPanel role="menu" aria-label="Process actions">
                              <StatusMenuOption
                                type="button"
                                role="menuitem"
                                onClick={startEdit}
                              >
                                Edit
                              </StatusMenuOption>
                              <StatusMenuOption
                                type="button"
                                role="menuitem"
                                onClick={toggleBookmark}
                              >
                                {currentProcess?.isBookmarked ? 'Remove Bookmark' : 'Bookmark'}
                              </StatusMenuOption>
                              <StatusMenuOption
                                type="button"
                                role="menuitem"
                                $danger
                                onClick={deleteCurrentProcess}
                              >
                                Delete
                              </StatusMenuOption>
                            </StatusMenuPanel>
                          ) : null}
                        </ActionMenuWrap>
                      )}
                    </div>
                  </PanelTitleRow>
                  <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.74rem', fontWeight: 700 }}>
                    
                  </div>
                </PanelHeader>

                <EditorToolbar>
                  <SmallButton type="button" title="Bold" aria-label="Bold" disabled={!isEditing} onClick={() => exec('bold')}><Bold size={14} /></SmallButton>
                  <SmallButton type="button" title="Italic" aria-label="Italic" disabled={!isEditing} onClick={() => exec('italic')}><Italic size={14} /></SmallButton>
                  <SmallButton type="button" title="Heading 1" aria-label="Heading 1" disabled={!isEditing} onClick={() => exec('formatBlock', '<h1>')}><Heading1 size={14} /></SmallButton>
                  <SmallButton type="button" title="Heading 2" aria-label="Heading 2" disabled={!isEditing} onClick={() => exec('formatBlock', '<h2>')}><Heading2 size={14} /></SmallButton>
                  <SmallButton type="button" title="Bulleted list" aria-label="Bulleted list" disabled={!isEditing} onClick={() => exec('insertUnorderedList')}><List size={14} /></SmallButton>
                  <SmallButton type="button" title="Blockquote" aria-label="Blockquote" disabled={!isEditing} onClick={() => exec('formatBlock', '<blockquote>')}><MessageSquareQuote size={14} /></SmallButton>
                  <SmallButton type="button" title="Insert link" aria-label="Insert link" disabled={!isEditing} onClick={() => exec('createLink', 'https://')}><Link2 size={14} /></SmallButton>
                </EditorToolbar>

                <div style={{ padding: '0 18px 14px', color: 'rgba(255,255,255,0.48)', fontSize: '0.74rem', fontWeight: 700 }}>
                  
                </div>

                <EditorSurface
                  ref={editorRef}
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  spellCheck
                  onInput={(event) => {
                    if (!isEditing) return;
                    updateDraft((current) => ({
                      ...current,
                      bodyHtml: event.currentTarget.innerHTML,
                    }));
                  }}
                  style={{ cursor: isEditing ? 'text' : 'default' }}
                />
              </EditorColumn>

              <Inspector>
                <InspectorTabs>
                  <InspectorTabButton type="button" $active={inspectorTab === 'details'} onClick={() => setInspectorTab('details')}>
                    Details
                  </InspectorTabButton>
                  <InspectorTabButton type="button" $active={inspectorTab === 'execution'} onClick={() => setInspectorTab('execution')}>
                    Execution
                  </InspectorTabButton>
                  <InspectorTabButton type="button" $active={inspectorTab === 'guardrails'} onClick={() => setInspectorTab('guardrails')}>
                    Guardrails
                  </InspectorTabButton>
                  <InspectorTabButton type="button" $active={inspectorTab === 'revisions'} onClick={() => setInspectorTab('revisions')}>
                    Revision Logs
                  </InspectorTabButton>
                  <InspectorTabButton type="button" $active={inspectorTab === 'resolution'} onClick={() => setInspectorTab('resolution')}>
                    Resolution Summary
                  </InspectorTabButton>
                </InspectorTabs>

                <InspectorCard style={{ minHeight: 0, overflow: 'auto', alignContent: 'start' }}>
                  {inspectorTab === 'details' ? (
                    <div style={{ display: 'grid', gap: 18 }}>
                      <div style={{ display: 'grid', gap: 12 }}>
                        {isLockedServiceProcess ? (
                          <>
                            <ReadOnlyField>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Linked service
                              </div>
                              <ReadOnlyValue>{draftProcess?.serviceLabel ?? currentProcess.serviceLabel ?? 'No linked service'}</ReadOnlyValue>
                            </ReadOnlyField>
                            <ReadOnlyField>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Category
                              </div>
                              <ReadOnlyValue>{draftProcess?.category ?? currentProcess.category}</ReadOnlyValue>
                            </ReadOnlyField>
                            <ReadOnlyField>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Subcategories
                              </div>
                              {currentEditableSubcategories.length ? (
                                currentEditableSubcategories.map((subcategory) => (
                                  <ReadOnlyValue key={subcategory}>{subcategory}</ReadOnlyValue>
                                ))
                              ) : (
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', fontWeight: 700 }}>
                                  No subcategories linked.
                                </div>
                              )}
                            </ReadOnlyField>
                          </>
                        ) : isEditing ? (
                          <>
                            <div style={{ display: 'grid', gap: 8 }}>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Linked service
                              </div>
                              <PicklistControl>
                                <GlassPicklist
                                  value={draftProcess?.serviceCode ?? currentProcess.serviceCode ?? ''}
                                  options={editorServiceOptions}
                                  onChange={(nextValue) => {
                                    const nextServiceCode = nextValue.trim() || null;
                                    const nextService = nextServiceCode ? serviceOptionByCode.get(nextServiceCode) ?? null : null;
                                    updateDraft((current) => {
                                      const nextCategoryOptions = getCategoryOptionsForService(nextServiceCode);
                                      const hasCurrentCategory = nextCategoryOptions.some(
                                        (option) => option.normalizedLabel === normalizeText(current.category),
                                      );
                                      const fallbackCategoryOption = hasCurrentCategory ? null : nextCategoryOptions[0] ?? null;
                                      const fallbackCategory = fallbackCategoryOption?.label ?? current.category;
                                      const fallbackSubcategories = fallbackCategory
                                        ? categorySubcategories[makeCategoryRegistryKey(nextServiceCode, fallbackCategory)] ?? current.subcategories
                                        : current.subcategories;

                                      return {
                                        ...current,
                                        category: fallbackCategory,
                                        serviceId: nextService?.id ?? null,
                                        serviceCode: fallbackCategoryOption?.serviceCode ?? nextServiceCode,
                                        serviceLabel: fallbackCategoryOption?.serviceTitle ?? nextService?.label ?? null,
                                        offeringCode: fallbackCategoryOption?.offeringCode ?? null,
                                        subcategoryId: null,
                                        subcategories: fallbackSubcategories,
                                      };
                                    });
                                  }}
                                  ariaLabel="Select linked service"
                                  placeholder="No linked service"
                                  searchable
                                  searchPlaceholder="Search services"
                                />
                              </PicklistControl>
                            </div>

                            <div style={{ display: 'grid', gap: 8 }}>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Category
                              </div>
                              <PicklistControl>
                                <GlassPicklist
                                  value={selectedCategoryOption?.label ?? (currentEditorCategoryLabel || '')}
                                  options={editorCategoryOptions}
                                  onChange={(nextValue) => {
                                    const nextCategoryOption = availableCategoryOptions.find((option) => option.label === nextValue);
                                    const nextCategoryLabel = nextCategoryOption?.label ?? nextValue ?? '';
                                    updateDraft((current) => ({
                                      ...current,
                                      category: nextCategoryLabel || current.category,
                                      serviceCode: nextCategoryOption?.serviceCode ?? current.serviceCode,
                                      serviceLabel: nextCategoryOption?.serviceTitle ?? current.serviceLabel,
                                      offeringCode: nextCategoryOption?.offeringCode ?? current.offeringCode,
                                      subcategoryId: null,
                                      subcategories: nextCategoryLabel
                                        ? categorySubcategories[
                                          makeCategoryRegistryKey(
                                            nextCategoryOption?.serviceCode ?? current.serviceCode,
                                            nextCategoryLabel,
                                          )
                                        ] ?? current.subcategories
                                        : current.subcategories,
                                    }));
                                  }}
                                  ariaLabel="Select process category"
                                  placeholder="Select category"
                                  searchable
                                  searchPlaceholder="Search categories"
                                  emptyLabel="No categories available."
                                />
                              </PicklistControl>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                                <PlainInput
                                  value={draftProcess?.category ?? currentProcess.category}
                                  onChange={(event) =>
                                    updateDraft((current) => ({
                                      ...current,
                                      category: event.target.value,
                                    }))
                                  }
                                  placeholder="Category"
                                />
                                <SmallButton
                                  type="button"
                                  onClick={registerCurrentCategory}
                                  disabled={
                                    !draftProcess?.category.trim()
                                    || Boolean(
                                      draftProcess.category.trim()
                                      && categoryOptionByLabel.has(normalizeText(draftProcess.category)),
                                    )
                                  }
                                >
                                  Save Category
                                </SmallButton>
                              </div>
                            </div>

                            <div style={{ display: 'grid', gap: 8 }}>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Subcategories
                              </div>
                              {currentEditableSubcategories.length ? (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  {currentEditableSubcategories.map((subcategory) => (
                                    <span
                                      key={subcategory}
                                      style={{
                                        padding: '5px 9px',
                                        borderRadius: 999,
                                        border: '1px solid rgba(224, 161, 94, 0.28)',
                                        background: 'rgba(224, 161, 94, 0.12)',
                                        color: '#E8EAED',
                                        fontSize: '0.68rem',
                                        fontWeight: 800,
                                      }}
                                    >
                                      {subcategory}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', fontWeight: 700 }}>
                                  No subcategories linked.
                                </div>
                              )}
                              {availableSubcategories.length ? (
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                  {availableSubcategories.map((subcategory) => {
                                    const isSelected = currentEditableSubcategories.some(
                                      (value) => normalizeText(value) === normalizeText(subcategory),
                                    );
                                    return (
                                      <button
                                        key={subcategory}
                                        type="button"
                                        onClick={() =>
                                          updateDraft((current) => ({
                                            ...current,
                                            subcategories: isSelected
                                              ? current.subcategories.filter(
                                                (value) => normalizeText(value) !== normalizeText(subcategory),
                                              )
                                              : dedupeStrings([...current.subcategories, subcategory]),
                                          }))
                                        }
                                        style={{
                                          padding: '5px 9px',
                                          borderRadius: 999,
                                          border: isSelected
                                            ? '1px solid rgba(224, 161, 94, 0.55)'
                                            : '1px solid rgba(255,255,255,0.1)',
                                          background: isSelected
                                            ? 'rgba(224, 161, 94, 0.14)'
                                            : 'rgba(255,255,255,0.04)',
                                          color: '#E8EAED',
                                          fontSize: '0.68rem',
                                          fontWeight: 800,
                                          cursor: 'pointer',
                                        }}
                                      >
                                        {subcategory}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : null}
                              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                                <PlainInput
                                  value={newSubcategory}
                                  onChange={(event) => setNewSubcategory(event.target.value)}
                                  placeholder="Add subcategory"
                                />
                                <SmallButton
                                  type="button"
                                  onClick={addCustomSubcategory}
                                  disabled={!draftProcess?.category.trim() || !newSubcategory.trim()}
                                >
                                  Add Subcategory
                                </SmallButton>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {currentProcess.serviceLabel ? (
                              <ReadOnlyField>
                                <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                  Linked service
                                </div>
                                <ReadOnlyValue>{currentProcess.serviceLabel}</ReadOnlyValue>
                              </ReadOnlyField>
                            ) : null}
                            <ReadOnlyField>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Category
                              </div>
                              <ReadOnlyValue>{currentProcess.category}</ReadOnlyValue>
                            </ReadOnlyField>
                            <ReadOnlyField>
                              <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                Subcategories
                              </div>
                              {currentProcess.subcategories.length ? (
                                currentProcess.subcategories.map((subcategory) => (
                                  <ReadOnlyValue key={subcategory}>{subcategory}</ReadOnlyValue>
                                ))
                              ) : (
                                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', fontWeight: 700 }}>
                                  No subcategories linked.
                                </div>
                              )}
                            </ReadOnlyField>
                          </>
                        )}
                      </div>

                      <div style={{ display: 'grid', gap: 8 }}>
                        <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Process summary
                        </div>
                        {isEditing ? (
                          <PlainTextArea
                            value={draftProcess?.summary ?? currentProcess.summary}
                            onChange={(event) =>
                              updateDraft((current) => ({
                                ...current,
                                summary: event.target.value,
                              }))
                            }
                          />
                        ) : (
                          <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem', lineHeight: 1.6, fontWeight: 700 }}>
                            {currentProcess.summary || 'No process summary yet.'}
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'grid', gap: 8 }}>
                        <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Tags
                        </div>
                        {(isEditing ? currentEditableTags : currentProcess.tags).length ? (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {(isEditing ? currentEditableTags : currentProcess.tags).map((tag) => (
                              isEditing ? (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() =>
                                    updateDraft((current) => ({
                                      ...current,
                                      tags: current.tags.filter(
                                        (value) => normalizeText(value) !== normalizeText(tag),
                                      ),
                                    }))
                                  }
                                  style={{
                                    padding: '5px 9px',
                                    borderRadius: 999,
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    background: 'rgba(224, 161, 94, 0.14)',
                                    color: '#E8EAED',
                                    fontSize: '0.68rem',
                                    fontWeight: 800,
                                    cursor: 'pointer',
                                  }}
                                >
                                  #{tag} <span style={{ marginLeft: 4 }}>x</span>
                                </button>
                              ) : (
                                <span
                                  key={tag}
                                  style={{
                                    padding: '5px 9px',
                                    borderRadius: 999,
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    background: 'rgba(224, 161, 94, 0.14)',
                                    color: '#E8EAED',
                                    fontSize: '0.68rem',
                                    fontWeight: 800,
                                  }}
                                >
                                  #{tag}
                                </span>
                              )
                            ))}
                          </div>
                        ) : (
                          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', fontWeight: 700 }}>
                            No tags added yet.
                          </div>
                        )}
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                            <PlainInput
                              value={newTag}
                              onChange={(event) => setNewTag(event.target.value)}
                              placeholder="Add tag"
                            />
                            <SmallButton type="button" onClick={addCustomTag} disabled={!newTag.trim()}>
                              Add Tag
                            </SmallButton>
                          </div>
                        ) : null}
                      </div>

                      <div style={{ display: 'grid', gap: 8 }}>
                        <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          Linked processes
                        </div>
                        {linked.length ? linked.map((process) => (
                          <button
                            key={process.id}
                            type="button"
                            onClick={() => openProcess(process.id)}
                            style={{
                              textAlign: 'left',
                              borderRadius: 14,
                              border: '1px solid rgba(255,255,255,0.1)',
                              background: 'rgba(255,255,255,0.04)',
                              padding: 12,
                              color: 'rgba(255,255,255,0.9)',
                              cursor: 'pointer',
                            }}
                          >
                            <div style={{ fontSize: '0.8rem', fontWeight: 800 }}>{process.title}</div>
                            <div style={{ marginTop: 6, color: 'rgba(255,255,255,0.56)', fontSize: '0.72rem', fontWeight: 700 }}>
                              {process.serviceLabel ?? 'General'} / {process.category}
                            </div>
                          </button>
                        )) : (
                          <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.82rem', fontWeight: 700 }}>
                            No linked knowledge yet.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : inspectorTab === 'execution' ? (
                    <div style={{ display: 'grid', gap: 18 }}>
                      {EXECUTION_TEXT_FIELDS.map((field) => {
                        const currentValue = draftProcess?.[field.key] ?? currentProcess[field.key] ?? '';
                        return (
                          <div key={field.key} style={{ display: 'grid', gap: 8 }}>
                            <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              {field.label}
                            </div>
                            {isEditing ? (
                              <PlainTextArea
                                value={currentValue}
                                onChange={(event) => updateDraftTextField(field.key, event.target.value)}
                                placeholder={field.placeholder}
                                style={{ minHeight: 96 }}
                              />
                            ) : (
                              <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem', lineHeight: 1.6, fontWeight: 700 }}>
                                {currentValue || `No ${field.label.toLowerCase()} defined yet.`}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {EXECUTION_LIST_FIELDS.map((field) => {
                        const currentValues = (draftProcess?.[field.key] ?? currentProcess[field.key] ?? []) as string[];
                        return (
                          <div key={field.key} style={{ display: 'grid', gap: 8 }}>
                            <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              {field.label}
                            </div>
                            {isEditing ? (
                              <PlainTextArea
                                value={formatListField(currentValues)}
                                onChange={(event) => updateDraftListField(field.key, event.target.value)}
                                placeholder={field.placeholder}
                              />
                            ) : (
                              renderReadOnlyList(currentValues, `No ${field.label.toLowerCase()} defined yet.`)
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : inspectorTab === 'guardrails' ? (
                    <div style={{ display: 'grid', gap: 18 }}>
                      {GUARDRAIL_LIST_FIELDS.map((field) => {
                        const currentValues = (draftProcess?.[field.key] ?? currentProcess[field.key] ?? []) as string[];
                        return (
                          <div key={field.key} style={{ display: 'grid', gap: 8 }}>
                            <div style={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                              {field.label}
                            </div>
                            {isEditing ? (
                              <PlainTextArea
                                value={formatListField(currentValues)}
                                onChange={(event) => updateDraftListField(field.key, event.target.value)}
                                placeholder={field.placeholder}
                              />
                            ) : (
                              renderReadOnlyList(currentValues, `No ${field.label.toLowerCase()} defined yet.`)
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : inspectorTab === 'revisions' ? (
                    <div style={{ display: 'grid', gap: 18 }}>
                      <div style={{ display: 'grid', gap: 10 }}>
                        <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.84rem', fontWeight: 900, textTransform: 'uppercase' }}>
                          Contributors
                        </div>
                        {currentProcess.contributors.map((contributor) => (
                          <div key={contributor.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'center' }}>
                            <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, rgba(224,161,94,0.96), rgba(166,76,68,0.92))', color: '#111', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 900 }}>
                              {initials(contributor.name)}
                            </div>
                            <div>
                              <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.82rem', fontWeight: 800 }}>{contributor.name}</div>
                              <div style={{ color: 'rgba(255,255,255,0.54)', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>{contributor.role}</div>
                            </div>
                            <div style={{ color: backendPalette.yellow, fontSize: '0.72rem', fontWeight: 900 }}>{contributor.edits} edits</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'grid', gap: 10 }}>
                        <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.84rem', fontWeight: 900, textTransform: 'uppercase' }}>
                          Revision history
                        </div>
                        {currentProcess.revisions.map((revision) => (
                          <div key={revision.id} style={{ display: 'grid', gap: 4, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.78rem', fontWeight: 800 }}>{revision.author}</span>
                              <span style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.7rem', fontWeight: 700 }}>{revision.changedAt}</span>
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: '0.78rem', lineHeight: 1.5, fontWeight: 700 }}>{revision.summary}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 14 }}>
                      <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.84rem', fontWeight: 900, textTransform: 'uppercase' }}>
                        Resolution summary
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: '0.84rem', lineHeight: 1.6, fontWeight: 700 }}>
                        Lessons learned from completed tickets that use this process will appear here so the workflow can be refined over time.
                      </div>
                      <div style={{ border: '1px dashed rgba(255,255,255,0.14)', borderRadius: 12, padding: 14, color: 'rgba(255,255,255,0.54)', fontSize: '0.8rem', fontWeight: 700 }}>
                        No completed-ticket learnings are linked to this process yet.
                      </div>
                    </div>
                  )}
                </InspectorCard>
              </Inspector>
            </WorkspaceBody>
          ) : (
            <div style={{ padding: 24, color: 'rgba(255,255,255,0.68)', fontWeight: 700 }}>Open a process from the library to start working.</div>
          )}
        </WorkspacePanel>
      </Workspace>

      <ModalBackdrop
        type="button"
        $open={filterModalOpen}
        aria-label="Close filters modal"
        onClick={closeFilterModal}
      />
      <ModalPanel
        as={BaseCard}
        $open={filterModalOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Filter processes"
      >
        <ModalHeader>
          <ModalTitleRow>
            <ModalTitle>Filter Processes</ModalTitle>
            <SmallButton type="button" onClick={closeFilterModal}>
              <X size={14} />
            </SmallButton>
          </ModalTitleRow>
          <ModalHelp>
            Narrow the process library with explicit filters instead of keeping controls inline.
          </ModalHelp>
        </ModalHeader>

        <ModalBody>
          <CreateGrid>
            <ModalSection>
              <ModalLabel>Service</ModalLabel>
              <PicklistControl>
                <GlassPicklist
                  value={filterDraft.service}
                  options={serviceFilterOptions}
                  onChange={(nextValue) =>
                    setFilterDraft((current) => ({ ...current, service: nextValue }))
                  }
                  ariaLabel="Filter by service"
                  placeholder="All services"
                  searchable
                  searchPlaceholder="Search services"
                />
              </PicklistControl>
            </ModalSection>

            <ModalSection>
              <ModalLabel>Status</ModalLabel>
              <PicklistControl>
                <GlassPicklist
                  value={filterDraft.status}
                  options={statusFilterOptions}
                  onChange={(nextValue) =>
                    setFilterDraft((current) => ({ ...current, status: nextValue }))
                  }
                  ariaLabel="Filter by status"
                  placeholder="All statuses"
                />
              </PicklistControl>
            </ModalSection>
          </CreateGrid>

          <CreateGrid>
            <ModalSection>
              <ModalLabel>Process Type</ModalLabel>
              <PicklistControl>
                <GlassPicklist
                  value={filterDraft.processType}
                  options={processTypeFilterOptions}
                  onChange={(nextValue) =>
                    setFilterDraft((current) => ({ ...current, processType: nextValue }))
                  }
                  ariaLabel="Filter by process type"
                  placeholder="All process types"
                />
              </PicklistControl>
            </ModalSection>

            <ModalSection>
              <ModalLabel>Bookmark State</ModalLabel>
              <PicklistControl>
                <GlassPicklist
                  value={filterDraft.bookmark}
                  options={bookmarkFilterOptions}
                  onChange={(nextValue) =>
                    setFilterDraft((current) => ({ ...current, bookmark: nextValue }))
                  }
                  ariaLabel="Filter by bookmark state"
                  placeholder="All bookmark states"
                />
              </PicklistControl>
            </ModalSection>
          </CreateGrid>

          <ModalSection>
            <ModalLabel>Sort</ModalLabel>
            <PicklistControl>
              <GlassPicklist
                value={filterDraft.sort}
                options={sortModeOptions}
                onChange={(nextValue) =>
                  setFilterDraft((current) => ({ ...current, sort: nextValue }))
                }
                ariaLabel="Sort processes"
                placeholder="Most recent"
              />
            </PicklistControl>
          </ModalSection>
        </ModalBody>

        <ModalFooter>
          <ModalHelp>
            {activeFilterCount
              ? `${activeFilterCount} active filter${activeFilterCount === 1 ? '' : 's'} applied.`
              : 'No filters are currently applied.'}
          </ModalHelp>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SmallButton type="button" onClick={resetFilterDraft}>
              Reset
            </SmallButton>
            <SmallButton type="button" onClick={closeFilterModal}>
              Cancel
            </SmallButton>
            <SmallButton type="button" $accent onClick={applyFilterDraft}>
              Apply Filters
            </SmallButton>
          </div>
        </ModalFooter>
      </ModalPanel>

      <ModalBackdrop
        type="button"
        $open={createModalOpen}
        aria-label="Close create process modal"
        onClick={closeCreateModal}
      />
      <ModalPanel
        as={BaseCard}
        $open={createModalOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Create process"
      >
        <ModalHeader>
          <ModalTitleRow>
            <ModalTitle>Create Process</ModalTitle>
            <SmallButton type="button" onClick={closeCreateModal}>
              <X size={14} />
            </SmallButton>
          </ModalTitleRow>
          <ModalHelp>
            Choose whether this process is tied directly to a service ticket flow or is a general
            internal process. Service processes map to the same service, offering, and subcategory
            structure used by tickets.
          </ModalHelp>
        </ModalHeader>

        <ModalBody>
          <ModalSection>
            <ModalLabel>Process Type</ModalLabel>
            <TypeGrid>
              <TypeOption
                type="button"
                $active={createDraft.type === 'service'}
                onClick={() =>
                  updateCreateDraft((current) => ({
                    ...current,
                    type: 'service',
                    error: '',
                  }))
                }
              >
                <div style={{ fontSize: '0.9rem', fontWeight: 900 }}>Service Process</div>
                <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.8rem', lineHeight: 1.45, fontWeight: 700 }}>
                  Link this process directly to a service, offering, and subcategory so it can be
                  located quickly for ticket workflows later.
                </div>
              </TypeOption>
              <TypeOption
                type="button"
                $active={createDraft.type === 'internal'}
                onClick={() =>
                  updateCreateDraft((current) => ({
                    ...current,
                    type: 'internal',
                    error: '',
                  }))
                }
              >
                <div style={{ fontSize: '0.9rem', fontWeight: 900 }}>Internal Process</div>
                <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.8rem', lineHeight: 1.45, fontWeight: 700 }}>
                  Use this for shared internal workflows that are not tied to a single service.
                  Internal processes rely on tags for discovery.
                </div>
              </TypeOption>
            </TypeGrid>
          </ModalSection>

          {createDraft.type === 'service' ? (
            <>
              <ModalSection>
                <ModalLabel>Process Name</ModalLabel>
                <PlainInput
                  value={createDraft.title}
                  onChange={(event) =>
                    updateCreateDraft((current) => ({
                      ...current,
                      title: event.target.value,
                      error: '',
                    }))
                  }
                  placeholder="Name the service process"
                />
              </ModalSection>
              <CreateGrid>
                <ModalSection>
                  <ModalLabel>Service</ModalLabel>
                  <PicklistControl>
                    <GlassPicklist
                      value={createDraft.serviceCode}
                      options={createServiceOptions}
                      onChange={(nextValue) =>
                        updateCreateDraft((current) => ({
                          ...current,
                          serviceCode: nextValue,
                          offeringCode: '',
                          selectedSubcategoryId: '',
                          selectedSubcategoryName: '',
                          selectedSubcategoryDescription: '',
                          subcategoryNameDraft: '',
                          subcategoryDescriptionDraft: '',
                          showSubcategoryComposer: false,
                          error: '',
                        }))
                      }
                      ariaLabel="Select service"
                      placeholder="Select service"
                      searchable
                      searchPlaceholder="Search services"
                    />
                  </PicklistControl>
                </ModalSection>

                <ModalSection>
                  <ModalLabel>Offering / Category</ModalLabel>
                  <PicklistControl>
                    <GlassPicklist
                      value={createDraft.offeringCode}
                      options={createOfferingOptions}
                      onChange={(nextValue) =>
                        updateCreateDraft((current) => ({
                          ...current,
                          offeringCode: nextValue,
                          selectedSubcategoryId: '',
                          selectedSubcategoryName: '',
                          selectedSubcategoryDescription: '',
                          subcategoryNameDraft: '',
                          subcategoryDescriptionDraft: '',
                          showSubcategoryComposer: false,
                          error: '',
                        }))
                      }
                      ariaLabel="Select offering"
                      placeholder="Select offering"
                      searchable
                      searchPlaceholder="Search offerings"
                      emptyLabel={
                        createDraft.serviceCode
                          ? 'No offerings available for this service.'
                          : 'Select a service first.'
                      }
                    />
                  </PicklistControl>
                </ModalSection>
              </CreateGrid>

              <ModalSection>
                <ModalLabel>Subcategory</ModalLabel>
                <ModalHelp>
                  Select an existing subcategory for this offering, or add a new one inline. New
                  subcategories are written back into the service offering immediately.
                </ModalHelp>
                <InlineRow>
                  <PicklistControl>
                    <GlassPicklist
                      value={createDraft.selectedSubcategoryId}
                      options={createSubcategoryOptions}
                      onChange={(nextValue) => {
                        const matching = createSubcategoryPool.find(
                          (subcategory) => subcategory.id === nextValue,
                        );
                        if (!matching) return;
                        selectCreateSubcategory(matching);
                      }}
                      ariaLabel="Select subcategory"
                      placeholder="Add subcategory"
                      searchable
                      searchPlaceholder="Search subcategories"
                      emptyLabel={
                        createDraft.offeringCode
                          ? 'No subcategories available for this offering yet.'
                          : 'Select an offering first.'
                      }
                    />
                  </PicklistControl>
                  <InlineAction
                    type="button"
                    aria-label="Create subcategory"
                    onClick={() =>
                      updateCreateDraft((current) => ({
                        ...current,
                        showSubcategoryComposer: !current.showSubcategoryComposer,
                        error: '',
                      }))
                    }
                    disabled={!createDraft.offeringCode}
                  >
                    <Plus size={14} />
                  </InlineAction>
                </InlineRow>
                {createDraft.showSubcategoryComposer ? (
                  <CreateGrid>
                    <PlainInput
                      value={createDraft.subcategoryNameDraft}
                      onChange={(event) =>
                        updateCreateDraft((current) => ({
                          ...current,
                          subcategoryNameDraft: event.target.value,
                          error: '',
                        }))
                      }
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        event.preventDefault();
                        void createSubcategoryFromDraft();
                      }}
                      placeholder="Subcategory name"
                    />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                      <PlainInput
                        value={createDraft.subcategoryDescriptionDraft}
                        onChange={(event) =>
                          updateCreateDraft((current) => ({
                            ...current,
                            subcategoryDescriptionDraft: event.target.value,
                          }))
                        }
                        placeholder="Short description (optional)"
                      />
                      <SmallButton
                        type="button"
                        onClick={() => {
                          void createSubcategoryFromDraft();
                        }}
                        disabled={!createDraft.subcategoryNameDraft.trim() || createDraft.saving}
                      >
                        Add
                      </SmallButton>
                    </div>
                  </CreateGrid>
                ) : null}
              </ModalSection>
            </>
          ) : null}

          {createDraft.type === 'internal' ? (
            <>
              <ModalSection>
                <ModalLabel>Process Name</ModalLabel>
                <PlainInput
                  value={createDraft.title}
                  onChange={(event) =>
                    updateCreateDraft((current) => ({
                      ...current,
                      title: event.target.value,
                      error: '',
                    }))
                  }
                  placeholder="Name the internal process"
                />
              </ModalSection>
              <ModalSection>
                <ModalLabel>Tags</ModalLabel>
                <ModalHelp>
                  Add at least one tag. You can select existing tags or create a new tag by typing
                  and pressing Enter.
                </ModalHelp>
                {createDraft.tags.length ? (
                  <TokenWrap>
                    <TokenRow>
                      {createDraft.tags.map((tag) => (
                        <TokenChip key={tag} type="button" onClick={() => removeCreateTag(tag)}>
                          #{tag}
                          <X size={12} />
                        </TokenChip>
                      ))}
                    </TokenRow>
                  </TokenWrap>
                ) : null}
                <PlainInput
                  value={createDraft.tagQuery}
                  onChange={(event) =>
                    updateCreateDraft((current) => ({
                      ...current,
                      tagQuery: event.target.value,
                      error: '',
                    }))
                  }
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    addCreateTag();
                  }}
                  placeholder="Search or create tags"
                />
                <SuggestionList>
                  {filteredTagSuggestions.map((tag) => (
                    <SuggestionButton
                      key={tag}
                      type="button"
                      onClick={() =>
                        updateCreateDraft((current) => ({
                          ...current,
                          tags: dedupeStrings([...current.tags, tag]),
                          tagQuery: '',
                          error: '',
                        }))
                      }
                    >
                      <div style={{ fontSize: '0.8rem', fontWeight: 800 }}>#{tag}</div>
                      <div style={{ color: 'rgba(255,255,255,0.58)', fontSize: '0.72rem', fontWeight: 700 }}>
                        Existing tag
                      </div>
                    </SuggestionButton>
                  ))}
                  {createDraft.tagQuery.trim()
                    && !existingTagPool.some(
                      (tag) => normalizeText(tag) === normalizeText(createDraft.tagQuery),
                    ) ? (
                      <SuggestionButton type="button" $accent onClick={addCreateTag}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 800 }}>
                          Create "#{createDraft.tagQuery.trim()}"
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.58)', fontSize: '0.72rem', fontWeight: 700 }}>
                          Press Enter or click to add this tag.
                        </div>
                      </SuggestionButton>
                    ) : null}
                </SuggestionList>
              </ModalSection>
            </>
          ) : null}

          {createDraft.error ? <ErrorText>{createDraft.error}</ErrorText> : null}
        </ModalBody>

        <ModalFooter>
          <ModalHelp>
            {createDraft.type === 'service'
              ? 'Service processes are mapped directly to the same taxonomy used by tickets.'
              : createDraft.type === 'internal'
                ? `${createDraft.tags.length} tag${createDraft.tags.length === 1 ? '' : 's'} added`
                : 'Choose a process type to start the guided setup.'}
          </ModalHelp>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <SmallButton type="button" onClick={closeCreateModal}>
              Cancel
            </SmallButton>
            <SmallButton
              type="button"
              $accent
              onClick={() => {
                void startCreate();
              }}
              disabled={createDraft.saving}
            >
              {createDraft.saving ? 'Working...' : 'Create Process'}
            </SmallButton>
          </div>
        </ModalFooter>
      </ModalPanel>

      <ModalBackdrop
        type="button"
        $open={Boolean(deleteModalProcessId)}
        aria-label="Close delete process modal"
        onClick={closeDeleteModal}
      />
      <ModalPanel
        as={BaseCard}
        $open={Boolean(deleteModalProcessId)}
        role="dialog"
        aria-modal="true"
        aria-label="Delete process confirmation"
      >
        <ModalHeader>
          <ModalTitleRow>
            <ModalTitle>Delete Process</ModalTitle>
            <SmallButton type="button" onClick={closeDeleteModal}>
              <X size={14} />
            </SmallButton>
          </ModalTitleRow>
          <ModalHelp>
            This action permanently deletes the process, its revision history, and any linked KBase references.
          </ModalHelp>
        </ModalHeader>

        <ModalBody>
          <ModalSection>
            <ModalLabel>Confirmation phrase</ModalLabel>
            <ModalHelp>
              Type <span style={{ color: '#E8EAED', fontWeight: 900 }}>{deleteConfirmationPhrase || 'delete process title'}</span> to confirm deleting{' '}
              <span style={{ color: '#E8EAED', fontWeight: 900 }}>{pendingDeleteProcess?.title ?? 'this process'}</span>.
            </ModalHelp>
            <PlainInput
              value={deleteConfirmationValue}
              onChange={(event) => setDeleteConfirmationValue(event.target.value)}
              placeholder={deleteConfirmationPhrase || 'Type the confirmation phrase'}
              autoComplete="off"
              autoFocus
            />
          </ModalSection>
        </ModalBody>

        <ModalFooter>
          <ModalHelp>
            Deletion cannot be undone.
          </ModalHelp>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <SmallButton type="button" onClick={closeDeleteModal}>
              Cancel
            </SmallButton>
            <DangerButton
              type="button"
              onClick={confirmDeleteProcess}
              disabled={!deleteConfirmationPhrase || deleteConfirmationValue.trim() !== deleteConfirmationPhrase}
            >
              Delete Process
            </DangerButton>
          </div>
        </ModalFooter>
      </ModalPanel>
    </Page>
  );
}
